import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, getFirestore, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore';
import PrintBarcodeDialog from '../components/PrintBarcodeDialog';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  IconButton,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Autocomplete
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  PhotoCamera as PhotoCameraIcon,
  Settings as SettingsIcon,
  Undo as RefundIcon,
  AssignmentReturn as ReturnIcon,
  WhatsApp as WhatsAppIcon,
  Engineering as EngineeringIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Devices as DevicesIcon,
  Build as BuildIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { 
  updateMaintenancePayment,
  getMaintenanceById,
  updateMaintenanceStatus,
  addPartsToMaintenance,
  removePartFromMaintenance,
  processMaintenanceRefund,
  updateMaintenanceRecord
} from '../services/maintenanceService';
import { getAllInventoryItems } from '../services/inventoryService';
import { getCustomerById, updateCustomer } from '../services/customerService';
import { getSystemSettings } from '../services/systemSettingsService';
import { getAllDeviceSizes, getAllDeviceBrands } from '../services/settingsService';
import { DeviceSizeModel, DeviceBrandModel } from '../utils/types';
import { getTechniciansByBranch } from '../services/technicianService';
import { statusColors, statusLabels, statusIcons, statusTooltips } from '../utils/constants';
import { useActivityLogger } from '../hooks/useActivityLogger';
import { MaintenanceStatus, InventoryItem, MaintenanceRecord, PaymentStatus, Technician, DeviceSize } from '../utils/types';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { formatCurrency } from '../utils/numberUtils';
import { printTestInstructions } from '../utils/maintenanceTestHelpers';
import WebcamCapture from '../components/maintenance/WebcamCapture';
import MaintenanceImageGallery from '../components/maintenance/MaintenanceImageGallery';
import ImageProviderSettings from '../components/maintenance/ImageProviderSettings';
import EnhancedPartsSelector from '../components/maintenance/EnhancedPartsSelector';
import WhatsAppMessageSender from '../components/WhatsAppMessageSender';
import { hybridWhatsAppService } from '../services/hybridWhatsAppService';
import InitialCostDialog from '../components/maintenance/InitialCostDialog';
import EstimatedCostDialog from '../components/maintenance/EstimatedCostDialog';
import DeliveryPaymentDialog from '../components/maintenance/DeliveryPaymentDialog';
import CostSummaryCard from '../components/maintenance/CostSummaryCard';
import CustomerApprovalDialog from '../components/maintenance/CustomerApprovalDialog';
import PostDeliveryPaymentDialog from '../components/maintenance/PostDeliveryPaymentDialog';
import AdvancePaymentDialog from '../components/maintenance/AdvancePaymentDialog';
import PaymentHistoryDialog from '../components/maintenance/PaymentHistoryDialog';
import MaintenanceRefundDialog, { RefundData } from '../components/maintenance/MaintenanceRefundDialog';
import MaintenanceReturnDialog from '../components/maintenance/MaintenanceReturnDialog';
import PrintOptionsDialog from '../components/PrintOptionsDialog';
import { generateReceiptHTML, generateWarrantyHTML, generateInvoiceHTML, PrintType } from '../utils/printTemplates';

const MaintenanceDetailsPage: React.FC = () => {
  // جميع useState في الأعلى
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const activityLogger = useActivityLogger();

  // حالة المعرف الحقيقي للطلب
  const [realId, setRealId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState(false);

  // دالة للتحقق من حالة المخزون
  const checkInventoryStatus = async (itemId: string, warehouseId: string, requiredQuantity: number) => {
    try {
      const { getInventoryQuantityInWarehouse } = await import('../services/inventoryService');
      const availableQuantity = await getInventoryQuantityInWarehouse(itemId, warehouseId);

      console.log('📊 حالة المخزون:', {
        itemId,
        warehouseId,
        availableQuantity,
        requiredQuantity,
        sufficient: availableQuantity >= requiredQuantity
      });

      return {
        available: availableQuantity,
        required: requiredQuantity,
        sufficient: availableQuantity >= requiredQuantity
      };
    } catch (error) {
      console.error('❌ خطأ في التحقق من حالة المخزون:', error);
      return {
        available: 0,
        required: requiredQuantity,
        sufficient: false
      };
    }
  };

  const [record, setRecord] = useState<MaintenanceRecord | null>(null);
  const [customerData, setCustomerData] = useState<any>(null);
  const [systemSettings, setSystemSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openPartsDialog, setOpenPartsDialog] = useState(false);
  const [openEnhancedPartsDialog, setOpenEnhancedPartsDialog] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [openBarcodeDialog, setOpenBarcodeDialog] = useState(false);
  const [openWebcamDialog, setOpenWebcamDialog] = useState(false);
  const [openImageSettingsDialog, setOpenImageSettingsDialog] = useState(false);
  const [openInitialCostDialog, setOpenInitialCostDialog] = useState(false);
  const [openEstimatedCostDialog, setOpenEstimatedCostDialog] = useState(false);
  const [openDeliveryPaymentDialog, setOpenDeliveryPaymentDialog] = useState(false);
  const [openCustomerApprovalDialog, setOpenCustomerApprovalDialog] = useState(false);
  const [openPostDeliveryPaymentDialog, setOpenPostDeliveryPaymentDialog] = useState(false);
  const [openAdvancePaymentDialog, setOpenAdvancePaymentDialog] = useState(false);
  const [openPaymentHistoryDialog, setOpenPaymentHistoryDialog] = useState(false);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  
  // حوارات خيارات الطباعة
  const [openPrintOptionsDialog, setOpenPrintOptionsDialog] = useState(false);
  const [pendingPrintAction, setPendingPrintAction] = useState<'receipt' | 'warranty' | 'invoice' | null>(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '', size: '', brand: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [showTechnicianDialog, setShowTechnicianDialog] = useState(false);
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<Technician | null>(null);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [customAmount, setCustomAmount] = useState<number>(0);
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<MaintenanceStatus | ''>('');
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [selectedPart, setSelectedPart] = useState<string>('');
  const [partQuantity, setPartQuantity] = useState<number>(1);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [costAfterContact, setCostAfterContact] = useState<number>(0);
  const [showCostAfterContact, setShowCostAfterContact] = useState<boolean>(false);

  // حالات أحجام وماركات الأجهزة من الإعدادات
  const [deviceSizes, setDeviceSizes] = useState<DeviceSizeModel[]>([]);
  const [deviceBrands, setDeviceBrands] = useState<DeviceBrandModel[]>([]);
  const [loadingDeviceSettings, setLoadingDeviceSettings] = useState(false);
  const [paymentType, setPaymentType] = useState<'cash' | 'debt'>('cash');
  const [showPaymentDialog, setShowPaymentDialog] = useState<boolean>(false);
  const [maintenanceImages, setMaintenanceImages] = useState<any[]>([]);
  const [actualWarehouseName, setActualWarehouseName] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // تهيئة نموذج التعديل عند فتح الحوار
  useEffect(() => {
    if (openEditDialog && record) {
      console.log('🔧 تهيئة نموذج التعديل...');
      console.log('📋 بيانات الطلب:', record);
      console.log('👤 بيانات العميل:', customerData);

      const formData = {
        name: record.customerName || '',
        phone: customerData?.phone || '',
        size: record.devices?.[0]?.size || '',
        brand: record.devices?.[0]?.brand || ''
      };

      // التأكد من عدم وجود قيم undefined
      Object.keys(formData).forEach(key => {
        if ((formData as any)[key] === undefined) {
          (formData as any)[key] = '';
        }
      });

      console.log('📝 بيانات النموذج المهيأة:', formData);
      setEditForm(formData);
    }
  }, [openEditDialog, record, customerData]);

  // تحميل بيانات طلب الصيانة عند تحميل الصفحة
  useEffect(() => {
    console.log('🔍 MaintenanceDetailsPage useEffect - id:', id);
    console.log('🔍 window.location:', window.location.href);

    if (id) {
      // التحقق إذا كان المعرف يبدأ بـ order- (رقم طلب) أو معرف Firebase عادي
      if (id.startsWith('order-')) {
        const orderNumber = id.substring(6); // إزالة "order-"
        console.log('🔍 البحث برقم الطلب من الرابط:', orderNumber);
        loadMaintenanceRecordByOrderNumber(orderNumber);
      } else {
        console.log('🔍 تحميل الطلب بالمعرف:', id);
        setRealId(id);
      }
      loadDeviceSettings(); // تحميل إعدادات الأجهزة

      // طباعة تعليمات الاختبار في وضع التطوير
      if (process.env.NODE_ENV === 'development') {
        printTestInstructions(id);
      }
    }
  }, [id]);

  // تحميل بيانات طلب الصيانة برقم الطلب
  const loadMaintenanceRecordByOrderNumber = async (orderNumber: string) => {
    try {
      setLoading(true);
      console.log('🔍 البحث عن طلب الصيانة برقم الطلب:', orderNumber);

      // استيراد دالة البحث المباشرة من قاعدة البيانات
      const { query, where, getDocs, collection } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');

      const maintenanceCollection = collection(db, 'maintenance');
      const orderNum = parseInt(orderNumber);

      console.log('🔍 البحث برقم الطلب المحول:', orderNum);

      // البحث المباشر برقم الطلب
      const q = query(
        maintenanceCollection,
        where('orderNumber', '==', orderNum)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];

        // تحديد realId أولاً
        setRealId(doc.id);

        // تحميل البيانات باستخدام المعرف
        await loadMaintenanceRecord(doc.id);
        return;
      }

      // إذا لم يتم العثور على الطلب
      console.log('❌ لم يتم العثور على طلب برقم الطلب:', orderNumber);
      setRecord(null);
      setSnackbar({
        open: true,
        message: `لم يتم العثور على طلب صيانة برقم: ${orderNumber}`,
        severity: 'error'
      });
    } catch (error) {
      console.error('❌ خطأ في البحث برقم الطلب:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء البحث عن الطلب',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // تحميل فنيي الفرع
  const loadTechnicians = async (branchId: string) => {
    try {
      console.log('🔄 تحميل فنيي الفرع:', branchId);
      setLoadingTechnicians(true);

      const techniciansList = await getTechniciansByBranch(branchId, true);
      console.log('👨‍🔧 تم جلب الفنيين:', techniciansList.length, 'فني');
      console.log('📋 قائمة الفنيين:', techniciansList.map(t => ({ id: t.id, name: t.name, isActive: t.isActive })));

      setTechnicians(techniciansList);
    } catch (error) {
      console.error('❌ خطأ في تحميل الفنيين:', error);
      setTechnicians([]);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل قائمة الفنيين',
        severity: 'error'
      });
    } finally {
      setLoadingTechnicians(false);
    }
  };

  // تحميل اسم المخزن الفعلي
  const loadActualWarehouseName = async (warehouseId: string) => {
    try {
      if (!warehouseId) {
        setActualWarehouseName('غير محدد');
        return;
      }

      const { getWarehouseById } = await import('../services/inventoryService');
      const warehouse = await getWarehouseById(warehouseId);

      if (warehouse) {
        setActualWarehouseName(warehouse.name);
        console.log('✅ تم تحميل اسم المخزن:', warehouse.name);
      } else {
        setActualWarehouseName('مخزن غير موجود');
        console.warn('⚠️ لم يتم العثور على المخزن:', warehouseId);
      }
    } catch (error) {
      console.error('❌ خطأ في تحميل اسم المخزن:', error);
      setActualWarehouseName('خطأ في التحميل');
    }
  };

  // تحميل بيانات طلب الصيانة
  const loadMaintenanceRecord = async (recordId: string) => {
    try {
      setLoading(true);

      // تحميل طلب الصيانة (هذا هو الأهم)
      const data = await getMaintenanceById(recordId);

      if (!data) {
        setRecord(null);
        setSnackbar({
          open: true,
          message: 'لم يتم العثور على طلب الصيانة',
          severity: 'error'
        });
        return;
      }

      setRecord(data);

      // تحميل اسم المخزن الفعلي
      if (data.warehouseId) {
        await loadActualWarehouseName(data.warehouseId);
      }



      // تحميل البيانات الإضافية (لا نريد أن تؤثر أخطاؤها على تحميل الطلب الأساسي)

      // تحميل فنيي الفرع
      if (data.branchId) {
        console.log('🏢 معرف الفرع:', data.branchId);
        try {
          await loadTechnicians(data.branchId);
        } catch (error) {
          console.warn('⚠️ فشل في تحميل فنيي الفرع:', error);
        }
      } else {
        console.warn('⚠️ لا يوجد معرف فرع في طلب الصيانة');
      }

      // تحميل بيانات العميل
      if (data.customerId) {
        try {
          const customer = await getCustomerById(data.customerId);
          setCustomerData(customer);
        } catch (error) {
          console.warn('⚠️ فشل في تحميل بيانات العميل:', error);
          setCustomerData(null);
        }
      }

      // تحميل إعدادات النظام
      try {
        const settings = await getSystemSettings();
        setSystemSettings(settings);
      } catch (error) {
        console.warn('⚠️ فشل في تحميل إعدادات النظام:', error);
        setSystemSettings(null);
      }

      // تحديث حالة الصور
      if (data.images) {
        setMaintenanceImages(data.images);
      } else {
        setMaintenanceImages([]);
      }

      // تحديث الحالة المحددة
      setSelectedStatus(data.status);

      // تهيئة التكلفة بعد الاتصال بالعميل
      if (data.costAfterContact) {
        setCostAfterContact(data.costAfterContact);
        setShowCostAfterContact(true);
      } else {
        setCostAfterContact(data.finalCost || data.initialCost);
        setShowCostAfterContact(false);
      }



    } catch (error) {
      console.error('❌ خطأ في تحميل طلب الصيانة:', error);
      setRecord(null);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل بيانات طلب الصيانة',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // تحميل قطع الغيار المتاحة
  const loadInventoryItems = async () => {
    try {
      setLoadingInventory(true);
      const items = await getAllInventoryItems();
      setInventoryItems(items.filter(item => item.quantity > 0));
    } catch (error) {
      console.error('Error loading inventory items:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحميل قطع الغيار المتاحة',
        severity: 'error'
      });
    } finally {
      setLoadingInventory(false);
    }
  };

  // تحميل إعدادات الأجهزة (الأحجام والماركات)
  const loadDeviceSettings = async () => {
    try {
      setLoadingDeviceSettings(true);
      console.log('🔧 تحميل إعدادات الأجهزة...');

      // تحميل أحجام الأجهزة
      const sizes = await getAllDeviceSizes(true); // فقط النشطة
      setDeviceSizes(sizes);
      console.log('📏 تم تحميل أحجام الأجهزة:', sizes.length);

      // تحميل ماركات الأجهزة
      const brands = await getAllDeviceBrands(true); // فقط النشطة
      setDeviceBrands(brands);
      console.log('🏷️ تم تحميل ماركات الأجهزة:', brands.length);

    } catch (error) {
      console.error('❌ خطأ في تحميل إعدادات الأجهزة:', error);
      // استخدام القيم الافتراضية في حالة الفشل
      setDeviceSizes([
        { id: 'small', name: 'صغير', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'medium', name: 'متوسط', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'large', name: 'كبير', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'extraLarge', name: 'كبير جداً', isActive: true, createdAt: new Date(), updatedAt: new Date() }
      ]);
      setDeviceBrands([
        { id: 'samsung', name: 'Samsung', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'apple', name: 'Apple', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'huawei', name: 'Huawei', isActive: true, createdAt: new Date(), updatedAt: new Date() },
        { id: 'other', name: 'أخرى', isActive: true, createdAt: new Date(), updatedAt: new Date() }
      ]);
    } finally {
      setLoadingDeviceSettings(false);
    }
  };

  // تحديث حالة طلب الصيانة
  const handleUpdateStatus = async () => {
    console.log('🔄 بدء تحديث حالة الطلب:', {
      realId,
      selectedStatus,
      recordExists: !!record,
      currentStatus: record?.status
    });

    if (!realId || !selectedStatus || !record) {
      console.warn('⚠️ بيانات مفقودة:', { realId, selectedStatus, record: !!record });
      return;
    }

    // إذا تم اختيار "تم التسليم"، افتح حوار الدفع والتسليم
    if (selectedStatus === 'delivered') {
      setOpenStatusDialog(false);
      setOpenDeliveryPaymentDialog(true);
      return;
    }

    const oldStatus = record.status;
    const oldStatusLabel = statusLabels[oldStatus] || oldStatus;
    const newStatusLabel = statusLabels[selectedStatus] || selectedStatus;

    try {
      console.log('📡 استدعاء updateMaintenanceStatus:', { realId, selectedStatus });
      await updateMaintenanceStatus(realId, selectedStatus);
      console.log('✅ تم تحديث الحالة بنجاح في قاعدة البيانات');

      // تسجيل النشاط
      await activityLogger.logMaintenanceStatusChange(
        realId,
        record.customerName,
        record.deviceType,
        oldStatusLabel,
        newStatusLabel
      );

      setSnackbar({
        open: true,
        message: 'تم تحديث حالة طلب الصيانة بنجاح',
        severity: 'success'
      });

      // إعادة تحميل بيانات طلب الصيانة
      console.log('🔄 إعادة تحميل بيانات الطلب...');
      await loadMaintenanceRecord(realId);
      console.log('✅ تم إعادة تحميل البيانات');

      // إغلاق نافذة الحوار
      setOpenStatusDialog(false);
    } catch (error) {
      console.error('Error updating maintenance status:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث حالة طلب الصيانة',
        severity: 'error'
      });
    }
  };

  // إضافة قطعة غيار إلى طلب الصيانة (الطريقة القديمة)
  const handleAddPart = async () => {
    if (!realId || !selectedPart || partQuantity <= 0) return;

    try {
      // Obtener directamente el elemento del inventario desde Firestore
      const inventoryService = await import('../services/inventoryService');
      const selectedItem = await inventoryService.getInventoryItemById(selectedPart);

      console.log('Selected item from Firestore:', selectedItem);

      if (!selectedItem) {
        throw new Error('لم يتم العثور على قطعة الغيار المحددة');
      }

      // Verificar si el precio está definido
      if (selectedItem.purchasePrice === undefined || selectedItem.purchasePrice === null) {
        console.warn('El precio de compra no está definido para este artículo:', selectedItem);
        // Intentar obtener el precio de otra manera
        const docRef = doc(getFirestore(), 'inventory', selectedPart);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const rawData = docSnap.data();
          console.log('Raw data from Firestore:', rawData);
          selectedItem.purchasePrice = rawData.purchasePrice || 0;
        }
      }

      console.log('Item price from Firestore:', selectedItem.purchasePrice);

      // Usar el precio de venta en lugar del precio de compra
      const price = Number(selectedItem.sellingPrice) || 0;
      console.log('Precio de venta convertido a número:', price);

      if (price === 0) {
        const confirmAdd = window.confirm('سعر قطعة الغيار هو 0. هل تريد المتابعة؟');
        if (!confirmAdd) {
          return;
        }
      }

      const partToAdd = {
        itemId: selectedPart,
        quantity: partQuantity,
        name: selectedItem.name,
        purchasePrice: Number(selectedItem.purchasePrice) || 0, // Guardar el precio de compra para informes
        sellingPrice: price // Usar el precio de venta para el cliente
      };

      console.log('Part to add:', partToAdd);

      await addPartsToMaintenance(realId!, [partToAdd], currentUser?.id);

      setSnackbar({
        open: true,
        message: 'تمت إضافة قطعة الغيار بنجاح',
        severity: 'success'
      });

      // إعادة تحميل بيانات طلب الصيانة
      loadMaintenanceRecord(realId);

      // إعادة تعيين الحقول
      setSelectedPart('');
      setPartQuantity(1);

      // إغلاق نافذة الحوار
      setOpenPartsDialog(false);
    } catch (error) {
      console.error('Error adding part to maintenance:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إضافة قطعة الغيار',
        severity: 'error'
      });
    }
  };

  // إضافة قطعة غيار من المكون المحسن
  const handleEnhancedPartSelect = async (part: InventoryItem, quantity: number) => {
    if (!realId) return;

    try {
      console.log('🔧 بدء إضافة قطعة غيار محسنة:', { partId: part.id, partName: part.name, quantity });

      const partToAdd = {
        itemId: part.id,
        quantity: quantity,
        name: part.name,
        purchasePrice: Number(part.purchasePrice) || 0,
        sellingPrice: Number(part.sellingPrice) || Number(part.purchasePrice) || 0
      };

      console.log('📦 بيانات القطعة المراد إضافتها:', partToAdd);

      // التحقق من توفر الكمية قبل الإضافة
      if (record?.warehouseId) {
        console.log('🔍 التحقق من توفر الكمية في المخزن...');
        // يمكن إضافة تحقق إضافي هنا إذا لزم الأمر
      }

      await addPartsToMaintenance(realId, [partToAdd], currentUser?.id);

      setSnackbar({
        open: true,
        message: `تمت إضافة ${part.name} (${quantity} وحدة) بنجاح وخصمها من المخزون`,
        severity: 'success'
      });

      // إعادة تحميل بيانات طلب الصيانة
      console.log('🔄 إعادة تحميل بيانات طلب الصيانة...');
      await loadMaintenanceRecord(realId);

      // إغلاق نافذة الحوار
      setOpenEnhancedPartsDialog(false);
    } catch (error: any) {
      console.error('❌ خطأ في إضافة قطعة الغيار:', error);
      setSnackbar({
        open: true,
        message: `فشل في إضافة قطعة الغيار: ${error.message || 'خطأ غير معروف'}`,
        severity: 'error'
      });
    }
  };

  // إزالة قطعة غيار من طلب الصيانة
  const handleRemovePart = async (partId: string) => {
    if (!id || !partId) {
      console.error('❌ معرف الصيانة أو معرف القطعة مفقود');
      return;
    }

    // العثور على القطعة المراد حذفها للحصول على اسمها
    const partToRemove = record?.usedParts?.find((part: any) => part.id === partId || part.itemId === partId);
    const partName = partToRemove?.name || 'قطعة غيار';

    console.log('🗑️ محاولة إزالة قطعة:', { partId, partName, maintenanceId: id });
    console.log('📋 القطعة المراد حذفها:', partToRemove);

    if (record && record.usedParts) {
      console.log('📦 قطع الغيار الحالية:', record.usedParts);
    }

    if (window.confirm(`هل أنت متأكد من حذف "${partName}"؟\n\nسيتم إعادة الكمية إلى المخزون تلقائياً.`)) {
      try {
        console.log('🔄 بدء عملية الحذف...');

        // استدعاء الدالة لحذف قطعة الغيار مع تمرير معرف المستخدم
        await removePartFromMaintenance(realId!, partId, currentUser?.id);
        console.log('✅ تمت عملية الحذف بنجاح');

        setSnackbar({
          open: true,
          message: `تمت إزالة "${partName}" بنجاح وإعادتها إلى المخزون`,
          severity: 'success'
        });

        // إعادة تحميل بيانات طلب الصيانة
        console.log('🔄 إعادة تحميل بيانات طلب الصيانة...');
        if (realId) await loadMaintenanceRecord(realId);
        console.log('✅ تم تحديث بيانات طلب الصيانة');
      } catch (error: any) {
        console.error('❌ خطأ في إزالة قطعة الغيار:', error);
        setSnackbar({
          open: true,
          message: `فشل في إزالة قطعة الغيار: ${error.message || 'خطأ غير معروف'}`,
          severity: 'error'
        });
      }
    }
  };



  // إضافة دفعة للصيانة
  const handleAddPayment = async () => {
    if (!record || !realId) {
      return;
    }

    // Si solo estamos actualizando el costo después de contactar y no hay pago
    if (paymentAmount <= 0 && showCostAfterContact && costAfterContact > 0) {
      try {
        // Actualizar solo el costo después de contactar y la opción de contactar antes de la reparación
        await updateMaintenancePayment(
          realId,
          {
            amount: 0, // No payment, just updating cost
            paymentType: 'cash',
            userId: currentUser?.id || '',
            userName: currentUser?.name || '',
            notes: 'تحديث التكلفة بعد الاتصال بالعميل'
          }
        );

        // Update the cost after contact separately
        await updateMaintenanceRecord(realId, {
          costAfterContact,
          requireCallBeforeMaintenance: record.requireCallBeforeMaintenance
        });

        setSnackbar({
          open: true,
          message: 'تم تحديث التكلفة بنجاح',
          severity: 'success'
        });

        // إعادة تحميل بيانات طلب الصيانة
        loadMaintenanceRecord(realId);

        // إعادة تعيين الحقول
        setCostAfterContact(0);
        setShowCostAfterContact(false);

        // إغلاق نافذة الحوار
        setOpenPaymentDialog(false);
        return;
      } catch (error) {
        console.error('Error updating cost after contact:', error);
        setSnackbar({
          open: true,
          message: 'حدث خطأ أثناء تحديث التكلفة',
          severity: 'error'
        });
        return;
      }
    }

    // Si estamos agregando un pago, verificar que sea mayor que cero
    if (paymentAmount <= 0) {
      return;
    }

    try {
      // المعلمات: معرف الصيانة، المبلغ المدفوع، التكلفة النهائية، حالة الدفع
      let finalCost = record.finalCost || record.initialCost;

      // Si se ha mostrado y establecido el costo después de contactar al cliente, usarlo como costo final
      if (showCostAfterContact && costAfterContact > 0) {
        finalCost = costAfterContact;
      }

      const newTotalPaid = (record.paid || 0) + paymentAmount;
      const paymentStatus: PaymentStatus =
        newTotalPaid >= finalCost
          ? 'fullyPaid'
          : newTotalPaid > 0
            ? 'partiallyPaid'
            : 'unpaid';

      await updateMaintenancePayment(
        realId,
        {
          amount: paymentAmount,
          paymentType: paymentType as 'cash' | 'debt',
          userId: currentUser?.id || '',
          userName: currentUser?.name || '',
          notes: `دفعة من ${record.customerName} - ${record.deviceType}`
        }
      );

      // Update other fields separately if needed
      if (showCostAfterContact && costAfterContact > 0) {
        await updateMaintenanceRecord(realId, {
          costAfterContact,
          requireCallBeforeMaintenance: record.requireCallBeforeMaintenance
        });
      }

      setSnackbar({
        open: true,
        message: 'تمت إضافة الدفعة بنجاح',
        severity: 'success'
      });

      // إعادة تحميل بيانات طلب الصيانة
      loadMaintenanceRecord(realId);

      // إعادة تعيين الحقول
      setPaymentAmount(0);
      setCostAfterContact(0);
      setShowCostAfterContact(false);

      // إغلاق نافذة الحوار
      setOpenPaymentDialog(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء إضافة الدفعة',
        severity: 'error'
      });
    }
  };

  // حساب المبلغ المتبقي
  const calculateRemainingAmount = (): number => {
    if (!record) return 0;

    // Si hay un costo después de contactar al cliente, usarlo para calcular el monto restante
    if (record.costAfterContact !== undefined && record.costAfterContact > 0) {
      return Math.max(0, record.costAfterContact - record.paid);
    }

    const finalCost = record.finalCost || record.initialCost || 0;
    return Math.max(0, finalCost - record.paid);
  };

  // معالجة مرتجع الصيانة
  const handleRefund = async (refundData: RefundData) => {
    try {
      console.log('🔄 بدء معالجة الإرجاع:', refundData);

      await processMaintenanceRefund(refundData);

      console.log('✅ تم إرجاع المبلغ بنجاح');
      setSnackbar({
        open: true,
        message: 'تم إرجاع المبلغ بنجاح وتحديث الدرج',
        severity: 'success'
      });

      // إعادة تحميل بيانات الصيانة
      if (realId) {
        console.log('🔄 إعادة تحميل بيانات الصيانة...');
        loadMaintenanceRecord(realId);
      }

      setOpenRefundDialog(false);
    } catch (error) {
      console.error('❌ خطأ في معالجة الإرجاع:', error);

      // تحديد نوع الخطأ لرسالة أكثر وضوحاً
      let errorMessage = 'حدث خطأ أثناء معالجة الإرجاع';

      if (error instanceof Error) {
        console.error('❌ تفاصيل الخطأ:', error.message);
        if (error.message.includes('permission')) {
          errorMessage = 'ليس لديك صلاحية لإجراء هذه العملية';
        } else if (error.message.includes('network')) {
          errorMessage = 'خطأ في الاتصال بالشبكة';
        } else if (error.message.includes('not found')) {
          errorMessage = 'لم يتم العثور على البيانات المطلوبة';
        } else {
          errorMessage = `خطأ: ${error.message}`;
        }
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };

  // فتح نافذة حوار قطع الغيار وتحميل البيانات
  const handleOpenPartsDialog = () => {
    loadInventoryItems();
    setOpenPartsDialog(true);
  };

  // رجوع إلى صفحة الصيانة
  const handleGoBack = () => {
    navigate('/admin/maintenance');
  };

  // دوال الطباعة
  const handlePrintReceipt = async () => {
    if (!record) return;
    setPendingPrintAction('receipt');
    setOpenPrintOptionsDialog(true);
  };

  const handlePrintWarranty = async () => {
    if (!record) return;
    setPendingPrintAction('warranty');
    setOpenPrintOptionsDialog(true);
  };

  const handlePrintInvoice = async () => {
    if (!record) return;
    setPendingPrintAction('invoice');
    setOpenPrintOptionsDialog(true);
  };

  // معالجة إنشاء الإرجاع
  const handleReturnCreated = () => {
    // إعادة تحميل بيانات الطلب لتحديث الحالة
    if (realId) {
      loadMaintenanceRecord(realId);
    }

    setSnackbar({
      open: true,
      message: 'تم إرجاع الجهاز بنجاح',
      severity: 'success'
    });
  };

  // دالة معالجة الطباعة مع خيارات النوع
  const handlePrintWithOptions = async (printType: PrintType) => {
    if (!record || !pendingPrintAction) return;

    try {
      // تسجيل النشاط
      const documentType = pendingPrintAction === 'receipt' ? 'إيصال استلام' : 
                          pendingPrintAction === 'warranty' ? 'شهادة ضمان' : 'فاتورة صيانة';
      
      await activityLogger.log(
        'DOCUMENT_PRINT' as any,
        `طباعة ${documentType}`,
        `تم طباعة ${documentType} لطلب الصيانة`,
        'maintenance',
        record.id,
        {
          level: 'LOW' as any,
          targetName: `${record.customerName} - ${record.deviceType}`,
          metadata: {
            documentType,
            printType
          }
        }
      );

      // إنشاء HTML حسب نوع الطباعة
      let htmlContent = '';
      const options = {
        printType,
        documentType: pendingPrintAction,
        systemSettings,
        customerData: customerData ? {
          phone: customerData.phone,
          alternatePhone: customerData.alternatePhone,
          email: customerData.email,
          address: customerData.address
        } : undefined
      };

      switch (pendingPrintAction) {
        case 'receipt':
          htmlContent = generateReceiptHTML(record, options);
          break;
        case 'warranty':
          htmlContent = generateWarrantyHTML(record, options);
          break;
        case 'invoice':
          htmlContent = generateInvoiceHTML(record, options);
          break;
        default:
          return;
      }

      // فتح نافذة الطباعة
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.print();
      }

      // إغلاق الحوارات
      setOpenPrintOptionsDialog(false);
      setPendingPrintAction(null);

    } catch (error) {
      console.error('خطأ في الطباعة:', error);
    }
  };

  // دوال إنشاء HTML للطباعة (تم نقلها إلى printTemplates.ts)
  const generateReceiptHTMLOld = () => {
    if (!record) return '';
    const currentDate = new Date().toLocaleDateString('ar-EG');
    const currentTime = new Date().toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
    const orderNumber = record.orderNumber || record.id.substring(0, 8);
    
    // حساب المبالغ المالية
    const initialCost = record.initialCost || 0;
    const finalCost = record.finalCost || initialCost;
    const totalPaid = record.totalPaid || record.paid || 0;
    const remainingAmount = record.remainingAmount || (finalCost - totalPaid);

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>إيصال استلام - ${orderNumber}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;600;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Cairo', sans-serif;
            padding: 8mm;
            line-height: 1.4;
            color: #000;
            background: #fff;
            font-size: 11pt;
          }
          
          /* رأس الإيصال */
          .header {
            text-align: center;
            border: 3px double #000;
            padding: 15px;
            margin-bottom: 15px;
            background: #f8f9fa;
          }
          
          .company-name {
            font-size: 22px;
            font-weight: 700;
            color: #000;
            margin-bottom: 5px;
          }
          
          .receipt-title {
            font-size: 18px;
            font-weight: 700;
            margin: 8px 0;
            color: #000;
          }
          
          .receipt-number {
            font-size: 14px;
            font-weight: 600;
            margin-top: 5px;
          }
          
          .date-time {
            font-size: 12px;
            color: #555;
            margin-top: 5px;
          }
          
          /* تخطيط عمود جانبي + محتوى رئيسي */
          .content {
            display: grid;
            grid-template-columns: 26% 1fr;
            gap: 10px;
            align-items: start;
          }
          .side-column {
            border: 2px solid #000;
            padding: 8px;
            background: #f8f9fa;
            display: flex;
            flex-direction: column;
            gap: 10px;
            min-height: 100%;
          }
          .side-box {
            border: 1px solid #000;
            padding: 8px;
            background: #fff;
            text-align: center;
          }
          .side-logo {
            width: 100%;
            max-height: 80px;
            object-fit: contain;
          }
          .side-vertical {
            writing-mode: vertical-rl;
            transform: rotate(180deg);
            border: 1px solid #000;
            background: #fff;
            padding: 8px 4px;
            text-align: center;
            font-weight: 700;
          }
          .barcode-wrap {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .main-column {
            display: block;
          }
          
          /* الجدول الرئيسي */
          .info-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            border: 2px solid #000;
          }
          
          .info-table th {
            background: #e9ecef;
            border: 1px solid #000;
            padding: 8px 6px;
            text-align: center;
            font-weight: 700;
            font-size: 12px;
          }
          
          .info-table td {
            border: 1px solid #000;
            padding: 8px 6px;
            font-size: 11px;
            text-align: center;
          }
          
          .info-table .label-cell {
            background: #f8f9fa;
            font-weight: 600;
            width: 30%;
          }
          
          .info-table .value-cell {
            background: #fff;
            text-align: right;
            padding-right: 10px;
          }
          
          /* جدول المالية */
          .financial-table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
            border: 2px solid #000;
          }
          
          .financial-table th {
            background: #dee2e6;
            border: 1px solid #000;
            padding: 10px;
            text-align: center;
            font-weight: 700;
            font-size: 13px;
          }
          
          .financial-table td {
            border: 1px solid #000;
            padding: 10px;
            text-align: center;
            font-size: 12px;
            font-weight: 600;
          }
          
          .financial-table .highlight {
            background: #d4edda;
            font-weight: 700;
            font-size: 13px;
          }
          
          .financial-table .warning {
            background: #fff3cd;
            font-weight: 700;
            font-size: 13px;
          }
          
          /* قسم الشروط */
          .terms-section {
            border: 2px solid #000;
            padding: 10px;
            margin: 15px 0;
            background: #f8f9fa;
          }
          
          .terms-title {
            font-weight: 700;
            font-size: 13px;
            margin-bottom: 8px;
            text-align: center;
            text-decoration: underline;
          }
          
          .terms-list {
            font-size: 10px;
            line-height: 1.6;
            padding-right: 20px;
          }
          
          .terms-list li {
            margin-bottom: 4px;
          }
          
          /* التوقيعات */
          .signature-section {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 15px;
            margin-top: 20px;
            text-align: center;
          }
          
          .signature-box {
            border: 2px solid #000;
            padding: 10px;
            min-height: 80px;
            background: #f8f9fa;
          }
          
          .signature-label {
            font-weight: 700;
            font-size: 11px;
            margin-bottom: 5px;
          }
          
          .signature-name {
            font-size: 10px;
            color: #555;
            margin-bottom: 30px;
          }
          
          .signature-line {
            border-top: 2px solid #000;
            padding-top: 5px;
            font-size: 10px;
            font-weight: 600;
          }
          
          /* التذييل */
          .footer {
            margin-top: 15px;
            text-align: center;
            border-top: 2px solid #000;
            padding-top: 10px;
            font-size: 10px;
          }
          
          .footer-highlight {
            font-weight: 700;
            font-size: 12px;
            margin: 5px 0;
          }
          
          /* للطباعة */
          @media print {
            body {
              padding: 5mm;
            }
            .no-print {
              display: none;
            }
          }
          
          /* تنسيق خاص للطابعة الحرارية 80mm */
          @page {
            size: 80mm auto;
            margin: 0;
          }
        </style>
      </head>
      <body>
        <!-- رأس الإيصال -->
        <div class="header">
          <div class="company-name">${systemSettings?.companyName || 'مركز الصيانة'}</div>
          <div class="receipt-title">إيصال استلام جهاز للصيانة</div>
          <div class="receipt-number">رقم الإيصال: ${orderNumber}</div>
          <div class="date-time">التاريخ: ${currentDate} | الوقت: ${currentTime}</div>
        </div>
        <div class="content">
          <!-- العمود الجانبي -->
          <div class="side-column">
            <div class="side-box">
              <img class="side-logo" src="${(systemSettings as any)?.logoUrl || (systemSettings as any)?.companyLogo || (systemSettings as any)?.logo || ''}" onerror="this.style.display='none'" />
            </div>
            <div class="side-box" id="qrcode" aria-label="QR Code"></div>
            <div class="side-box barcode-wrap">
              <svg id="barcode"></svg>
            </div>
            <div class="side-vertical">
              إيصال استلام | ${currentDate} | ${currentTime}
            </div>
          </div>
          <!-- المحتوى الرئيسي -->
          <div class="main-column">
        <!-- جدول معلومات العميل والجهاز -->
        <table class="info-table">
          <thead>
            <tr>
              <th colspan="4">بيانات العميل والجهاز</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="label-cell">اسم العميل</td>
              <td class="value-cell" colspan="3">${record.customerName}</td>
            </tr>
            <tr>
              <td class="label-cell">رقم الهاتف</td>
              <td class="value-cell">${customerData?.phone || customerData?.alternatePhone || (record as any)?.customerPhone || 'غير محدد'}</td>
              <td class="label-cell">تاريخ الاستلام</td>
              <td class="value-cell">${new Date(record.receivedAt).toLocaleDateString('ar-EG')}</td>
            </tr>
            <tr>
              <td class="label-cell">نوع الجهاز</td>
              <td class="value-cell">${record.deviceType}</td>
              <td class="label-cell">الماركة</td>
              <td class="value-cell">${record.devices?.[0]?.brand || 'غير محدد'}</td>
            </tr>
            <tr>
              <td class="label-cell">الموديل</td>
              <td class="value-cell">${record.devices?.[0]?.model || 'غير محدد'}</td>
              <td class="label-cell">الحجم</td>
              <td class="value-cell">${record.devices?.[0]?.size || 'غير محدد'}</td>
            </tr>
            ${record.devices?.[0]?.serialNumber ? `
            <tr>
              <td class="label-cell">الرقم التسلسلي</td>
              <td class="value-cell" colspan="3">${record.devices[0].serialNumber}</td>
            </tr>
            ` : ''}
            <tr>
              <td class="label-cell">وصف العطل</td>
              <td class="value-cell" colspan="3">${record.devices?.[0]?.issue || record.issue || 'غير محدد'}</td>
            </tr>
            ${record.accessories && record.accessories.length > 0 ? `
            <tr>
              <td class="label-cell">الملحقات</td>
              <td class="value-cell" colspan="3">${record.accessories.join(' - ')}</td>
            </tr>
            ` : ''}
            <tr>
              <td class="label-cell">الفني المسؤول</td>
              <td class="value-cell">${record.technicianName || 'لم يحدد بعد'}</td>
              <td class="label-cell">الموظف المستلم</td>
              <td class="value-cell">${record.receivedByEmployeeName || 'غير محدد'}</td>
            </tr>
            <tr>
              <td class="label-cell">التسليم المتوقع</td>
              <td class="value-cell" colspan="3">
                ${record.estimatedDeliveryDate ? new Date(record.estimatedDeliveryDate).toLocaleDateString('ar-EG') : 'سيتم تحديده بعد الفحص'}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- جدول المالية -->
        <table class="financial-table">
          <thead>
            <tr>
              <th colspan="4">البيانات المالية</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="font-weight: 700;">البيان</td>
              <td style="font-weight: 700;">التكلفة المبدئية</td>
              <td style="font-weight: 700;">المبلغ المدفوع</td>
              <td style="font-weight: 700;">المبلغ المتبقي</td>
            </tr>
            <tr>
              <td style="font-weight: 600;">المبلغ</td>
              <td>${initialCost.toFixed(2)} جنيه</td>
              <td class="highlight">${totalPaid.toFixed(2)} جنيه</td>
              <td class="${remainingAmount > 0 ? 'warning' : 'highlight'}">
                ${remainingAmount.toFixed(2)} جنيه
              </td>
            </tr>
          </tbody>
        </table>

        <!-- الشروط والأحكام -->
        <div class="terms-section">
          <div class="terms-title">شروط وأحكام الخدمة</div>
          <ul class="terms-list">
            <li>• يُرجى الاحتفاظ بهذا الإيصال لاستلام الجهاز</li>
            <li>• التكلفة النهائية قد تختلف بعد الفحص الفني للجهاز</li>
            <li>• سيتم إشعار العميل قبل البدء بأي صيانة تتجاوز التكلفة المبدئية</li>
            <li>• المركز غير مسؤول عن البيانات المخزنة على الجهاز</li>
            <li>• في حالة عدم استلام الجهاز خلال 3 أشهر، يحق للمركز التصرف فيه</li>
            <li>• الضمان على الصيانة: 6 أشهر (العيب نفسه فقط)</li>
          </ul>
        </div>

        <!-- منطقة التوقيعات -->
        <div class="signature-section">
          <div class="signature-box">
            <div class="signature-label">توقيع العميل</div>
            <div class="signature-name">${record.customerName}</div>
            <div class="signature-line">التوقيع</div>
          </div>
          <div class="signature-box">
            <div class="signature-label">ختم المركز</div>
            <div class="signature-name">&nbsp;</div>
            <div class="signature-line">الختم الرسمي</div>
          </div>
          <div class="signature-box">
            <div class="signature-label">الموظف المستلم</div>
            <div class="signature-name">${record.receivedByEmployeeName || 'غير محدد'}</div>
            <div class="signature-line">التوقيع</div>
          </div>
        </div>

        <!-- التذييل -->
        <div class="footer">
          <div class="footer-highlight">شكراً لثقتكم بنا</div>
          <div>📞 ${systemSettings?.phone || '0123456789'} ${systemSettings?.email ? `| 📧 ${systemSettings?.email}` : ''}</div>
          <div style="margin-top: 5px; font-size: 9px;">
            ${systemSettings?.address || ''} | تاريخ الطباعة: ${currentDate} ${currentTime}
          </div>
        </div>
      <!-- مكتبات QR و Barcode -->
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
      <script src="https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js"></script>
      <script>
        (function(){
          try {
            var order = String(${orderNumber});
            var verifyUrl = window.location.origin + '/verify/receipt/' + order;
            var qrEl = document.getElementById('qrcode');
            if (qrEl && window.QRCode) {
              new QRCode(qrEl, {
                text: verifyUrl,
                width: 96,
                height: 96,
                correctLevel: QRCode.CorrectLevel.M
              });
            }
            if (window.JsBarcode) {
              JsBarcode('#barcode', order, {format: 'CODE128', displayValue: true, fontSize: 12, height: 48, margin: 0});
            }
          } catch(e) { console && console.warn && console.warn('QR/Barcode error', e); }
        })();
      </script>
      </body>
      </html>
    `;
  };

  const generateWarrantyHTMLOld = () => {
    if (!record) return '';
    const currentDate = new Date().toLocaleDateString('ar-EG');
    const orderNumber = record.orderNumber || record.id.substring(0, 8);
    const warrantyEndDate = new Date();
    warrantyEndDate.setMonth(warrantyEndDate.getMonth() + 6); // ضمان 6 أشهر
    
    // حساب المبالغ المالية
    const initialCost = record.initialCost || 0;
    const finalCost = record.finalCost || initialCost;
    const totalPaid = record.totalPaid || 0;
    const remainingAmount = record.remainingAmount || (finalCost - totalPaid);

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>إيصال ضمان - ${orderNumber}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@300;400;600;700&display=swap');
          
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          body {
            font-family: 'Cairo', sans-serif;
            padding: 15mm;
            line-height: 1.8;
            color: #2c3e50;
            background: #ffffff;
          }
          
          /* رأس الإيصال */
          .header {
            text-align: center;
            border: 3px double #2c5aa0;
            padding: 25px;
            margin-bottom: 25px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 10px;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          }
          
          .company-name {
            font-size: 32px;
            font-weight: 700;
            margin-bottom: 8px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
          }
          
          .warranty-title {
            font-size: 26px;
            font-weight: 600;
            margin-bottom: 12px;
            background: rgba(255,255,255,0.2);
            padding: 8px 20px;
            border-radius: 20px;
            display: inline-block;
          }
          
          .warranty-number {
            font-size: 18px;
            font-weight: 600;
            letter-spacing: 1px;
            background: rgba(255,255,255,0.15);
            padding: 6px 15px;
            border-radius: 15px;
            display: inline-block;
          }
          
          /* شريط التاريخ */
          .date-bar {
            background: linear-gradient(90deg, #f093fb 0%, #f5576c 100%);
            color: white;
            padding: 15px;
            text-align: center;
            border-radius: 8px;
            margin-bottom: 25px;
            font-weight: 600;
            font-size: 16px;
            box-shadow: 0 3px 10px rgba(245, 87, 108, 0.3);
          }
          
          /* قسم المعلومات */
          .info-section {
            margin-bottom: 25px;
            border: 2px solid #e3f2fd;
            padding: 20px;
            border-radius: 10px;
            background: #f8f9fa;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          }
          
          .section-title {
            color: #2c5aa0;
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 18px;
            padding-bottom: 10px;
            border-bottom: 3px solid #667eea;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          
          .section-icon {
            width: 28px;
            height: 28px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
          }
          
          .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 12px 15px;
            margin-bottom: 8px;
            background: white;
            border-radius: 6px;
            border-right: 4px solid #667eea;
            transition: all 0.2s;
          }
          
          .info-row:hover {
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);
            transform: translateX(-3px);
          }
          
          .label {
            font-weight: 600;
            color: #495057;
            font-size: 15px;
            min-width: 140px;
          }
          
          .label::before {
            content: '●';
            color: #667eea;
            margin-left: 8px;
            font-size: 10px;
          }
          
          .value {
            color: #2c3e50;
            font-size: 15px;
            font-weight: 500;
            text-align: left;
            flex: 1;
          }
          
          /* قسم المالية */
          .financial-section {
            background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
            border: 3px solid #ff9a76;
            padding: 20px;
            border-radius: 12px;
            margin: 25px 0;
            box-shadow: 0 4px 15px rgba(255, 154, 118, 0.3);
          }
          
          .financial-title {
            color: #d84315;
            font-size: 22px;
            font-weight: 700;
            margin-bottom: 18px;
            text-align: center;
            text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
          }
          
          .financial-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 14px 18px;
            margin-bottom: 10px;
            background: white;
            border-radius: 8px;
            font-size: 17px;
            font-weight: 600;
            box-shadow: 0 2px 6px rgba(0,0,0,0.08);
          }
          
          .financial-label {
            color: #6d4c41;
          }
          
          .financial-value {
            color: #2e7d32;
            font-size: 19px;
            font-weight: 700;
          }
          
          .financial-value.negative {
            color: #c62828;
          }
          
          .financial-total {
            background: linear-gradient(90deg, #11998e 0%, #38ef7d 100%);
            color: white;
            font-size: 20px;
            padding: 16px 18px;
            box-shadow: 0 3px 10px rgba(17, 153, 142, 0.4);
          }
          
          .financial-remaining {
            background: linear-gradient(90deg, #eb3349 0%, #f45c43 100%);
            color: white;
            font-size: 20px;
            padding: 16px 18px;
            box-shadow: 0 3px 10px rgba(235, 51, 73, 0.4);
          }
          
          /* شريط صلاحية الضمان */
          .warranty-valid {
            background: linear-gradient(90deg, #56ab2f 0%, #a8e063 100%);
            color: white;
            padding: 18px;
            text-align: center;
            font-weight: 700;
            font-size: 19px;
            border-radius: 10px;
            margin: 25px 0;
            box-shadow: 0 4px 15px rgba(86, 171, 47, 0.4);
            letter-spacing: 0.5px;
          }
          
          /* شروط الضمان */
          .warranty-terms {
            background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
            padding: 20px;
            border-radius: 10px;
            margin: 25px 0;
            border: 2px solid #00acc1;
            box-shadow: 0 3px 10px rgba(0, 172, 193, 0.2);
          }
          
          .warranty-terms h3 {
            color: #006064;
            margin-bottom: 15px;
            font-size: 20px;
            font-weight: 700;
            padding-bottom: 10px;
            border-bottom: 2px solid #00acc1;
          }
          
          .warranty-terms ul {
            margin: 0;
            padding-right: 25px;
            list-style: none;
          }
          
          .warranty-terms li {
            margin-bottom: 10px;
            padding: 10px;
            background: white;
            border-radius: 6px;
            position: relative;
            padding-right: 30px;
            font-size: 14px;
            line-height: 1.6;
          }
          
          .warranty-terms li::before {
            content: '✓';
            position: absolute;
            right: 10px;
            color: #00acc1;
            font-weight: bold;
            font-size: 16px;
          }
          
          /* منطقة التوقيعات */
          .signature-section {
            margin-top: 40px;
            display: flex;
            justify-content: space-around;
            gap: 30px;
          }
          
          .signature-box {
            text-align: center;
            flex: 1;
            padding: 20px;
            background: #f8f9fa;
            border-radius: 10px;
            border: 2px dashed #dee2e6;
          }
          
          .signature-line {
            border-top: 3px solid #495057;
            margin-top: 50px;
            padding-top: 12px;
            font-weight: 700;
            color: #495057;
            font-size: 16px;
          }
          
          /* التذييل */
          .footer {
            margin-top: 40px;
            text-align: center;
            border-top: 3px double #667eea;
            padding-top: 20px;
            font-size: 14px;
            color: #6c757d;
            background: #f8f9fa;
            padding: 20px;
            border-radius: 10px;
          }
          
          .footer p {
            margin: 8px 0;
          }
          
          .footer strong {
            color: #2c5aa0;
            font-size: 16px;
          }
          
          /* فواصل جمالية */
          .divider {
            height: 2px;
            background: linear-gradient(90deg, transparent, #667eea, transparent);
            margin: 25px 0;
          }
          
          /* للطباعة */
          @media print {
            body { 
              margin: 0;
              padding: 10mm;
            }
            .no-print { display: none; }
            .info-row:hover {
              transform: none;
              box-shadow: none;
            }
          }
        </style>
      </head>
      <body>
        <!-- رأس الإيصال -->
        <div class="header">
          <div class="company-name">${systemSettings?.companyName || 'مركز الصيانة'}</div>
          <div class="warranty-title">📜 إيصال ضمان</div>
          <div class="warranty-number">رقم الإيصال: ${orderNumber}</div>
        </div>

        <!-- شريط التاريخ -->
        <div class="date-bar">
          📅 تاريخ الإصدار: ${currentDate} | ⏰ صالح حتى: ${warrantyEndDate.toLocaleDateString('ar-EG')}
        </div>

        <!-- قسم معلومات العميل -->
        <div class="info-section">
          <div class="section-title">
            <span class="section-icon">👤</span>
            معلومات العميل
          </div>
          <div class="info-row">
            <span class="label">اسم العميل</span>
            <span class="value">${record.customerName}</span>
          </div>
          <div class="info-row">
            <span class="label">رقم الهاتف</span>
            <span class="value">${customerData?.phone || customerData?.alternatePhone || 'غير محدد'}</span>
          </div>
          <div class="info-row">
            <span class="label">تاريخ الاستلام</span>
            <span class="value">${new Date(record.receivedAt).toLocaleDateString('ar-EG')}</span>
          </div>
        </div>

        <div class="divider"></div>

        <!-- قسم معلومات الجهاز -->
        <div class="info-section">
          <div class="section-title">
            <span class="section-icon">📱</span>
            معلومات الجهاز
          </div>
          <div class="info-row">
            <span class="label">نوع الجهاز</span>
            <span class="value">${record.deviceType}</span>
          </div>
          ${record.devices && record.devices[0]?.brand ? `
          <div class="info-row">
            <span class="label">الماركة</span>
            <span class="value">${record.devices[0].brand}</span>
          </div>
          ` : ''}
          ${record.devices && record.devices[0]?.model ? `
          <div class="info-row">
            <span class="label">الموديل</span>
            <span class="value">${record.devices[0].model}</span>
          </div>
          ` : ''}
          ${record.devices && record.devices[0]?.size ? `
          <div class="info-row">
            <span class="label">حجم الشاشة</span>
            <span class="value">${record.devices[0].size}</span>
          </div>
          ` : ''}
          ${record.devices && record.devices[0]?.serialNumber ? `
          <div class="info-row">
            <span class="label">الرقم التسلسلي</span>
            <span class="value">${record.devices[0].serialNumber}</span>
          </div>
          ` : ''}
          <div class="info-row">
            <span class="label">وصف العطل</span>
            <span class="value">${record.issue}</span>
          </div>
          ${record.diagnosis ? `
          <div class="info-row">
            <span class="label">التشخيص</span>
            <span class="value">${record.diagnosis}</span>
          </div>
          ` : ''}
          ${record.accessories && record.accessories.length > 0 ? `
          <div class="info-row">
            <span class="label">الملحقات</span>
            <span class="value">${record.accessories.join(' - ')}</span>
          </div>
          ` : ''}
        </div>

        <div class="divider"></div>

        <!-- قسم المالية -->
        <div class="financial-section">
          <div class="financial-title">💰 البيانات المالية</div>
          
          <div class="financial-row">
            <span class="financial-label">التكلفة المبدئية:</span>
            <span class="financial-value">${initialCost.toFixed(2)} جنيه</span>
          </div>
          
          ${finalCost !== initialCost ? `
          <div class="financial-row">
            <span class="financial-label">التكلفة النهائية:</span>
            <span class="financial-value">${finalCost.toFixed(2)} جنيه</span>
          </div>
          ` : ''}
          
          <div class="financial-row financial-total">
            <span class="financial-label">المبلغ المدفوع:</span>
            <span class="financial-value">${totalPaid.toFixed(2)} جنيه</span>
          </div>
          
          ${remainingAmount > 0 ? `
          <div class="financial-row financial-remaining">
            <span class="financial-label">المبلغ المتبقي:</span>
            <span class="financial-value negative">${remainingAmount.toFixed(2)} جنيه</span>
          </div>
          ` : `
          <div class="financial-row" style="background: linear-gradient(90deg, #11998e 0%, #38ef7d 100%); color: white;">
            <span class="financial-label">✓ تم السداد بالكامل</span>
            <span class="financial-value">0.00 جنيه</span>
          </div>
          `}
        </div>

        <div class="divider"></div>

        <!-- شروط الضمان -->
        <div class="warranty-terms">
          <h3>📋 شروط وأحكام الضمان</h3>
          <ul>
            <li>يسري هذا الضمان لمدة 6 أشهر من تاريخ الصيانة المُحدد أعلاه</li>
            <li>يغطي الضمان العيوب في الصيانة المنجزة فقط ولا يشمل عيوب جديدة</li>
            <li>لا يشمل الضمان الأضرار الناتجة عن سوء الاستخدام أو الحوادث أو الكسر</li>
            <li>يجب إحضار إيصال الضمان هذا والجهاز عند المطالبة بخدمة الضمان</li>
            <li>الضمان باطل في حالة العبث بالجهاز أو فتحه من قبل جهة غير مُعتمدة</li>
            <li>لا يشمل الضمان قطع الغيار المستبدلة إلا إذا نص على ذلك صراحة</li>
            <li>الضمان ساري فقط في فرع الصيانة الذي تم فيه العمل</li>
            <li>يحق للمركز رفض الضمان إذا ثبت سوء استخدام الجهاز</li>
          </ul>
        </div>

        <div class="divider"></div>

        <!-- منطقة التوقيعات -->
        <div class="signature-section">
          <div class="signature-box">
            <div style="font-size: 14px; color: #6c757d; margin-bottom: 10px;">الفني المسؤول</div>
            <div style="font-weight: 600; color: #2c5aa0; margin-bottom: 10px;">
              ${record.technicianName || record.receivedByEmployeeName || 'غير محدد'}
            </div>
            <div class="signature-line">التوقيع</div>
          </div>
          <div class="signature-box">
            <div style="font-size: 14px; color: #6c757d; margin-bottom: 10px;">ختم المركز</div>
            <div style="height: 60px; margin-bottom: 10px;"></div>
            <div class="signature-line">الختم الرسمي</div>
          </div>
          <div class="signature-box">
            <div style="font-size: 14px; color: #6c757d; margin-bottom: 10px;">استلام العميل</div>
            <div style="height: 60px; margin-bottom: 10px;"></div>
            <div class="signature-line">توقيع العميل</div>
          </div>
        </div>

        <!-- التذييل -->
        <div class="footer">
          <p><strong>🏢 ${systemSettings?.companyName || 'مركز الصيانة'}</strong></p>
          <p>خدمة صيانة احترافية وموثوقة</p>
          <p>
            📞 للاستفسارات: ${systemSettings?.phone || '0123456789'} 
            ${systemSettings?.email ? `| 📧 ${systemSettings?.email}` : ''}
          </p>
          <p style="font-size: 12px; color: #999; margin-top: 10px;">
            تاريخ الطباعة: ${currentDate} | رقم الطلب: ${orderNumber}
          </p>
          <p style="font-size: 13px; font-weight: 600; color: #667eea; margin-top: 15px;">
            💫 شكراً لثقتكم بنا - نتطلع لخدمتكم دائماً
          </p>
        </div>
      </body>
      </html>
    `;
  };

  const generateInvoiceHTMLOld = () => {
    if (!record) return '';
    const currentDate = new Date().toLocaleDateString('ar-EG');
    const orderNumber = record.orderNumber || record.id.substring(0, 8);
    const partsTotal = record.usedParts ? record.usedParts.reduce((total: number, part: any) => total + (part.quantity * (part.sellingPrice || part.purchasePrice || 0)), 0) : 0;
    const finalCost = record.finalCost || record.initialCost || 0;
    // التكلفة النهائية تشمل قطع الغيار بالفعل، لا نضيفها مرة أخرى
    const totalAmount = finalCost;

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="ar">
      <head>
        <meta charset="UTF-8">
        <title>فاتورة - ${orderNumber}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
          body {
            font-family: 'Cairo', sans-serif;
            margin: 20px;
            line-height: 1.6;
            color: #333;
          }
          .header {
            text-align: center;
            border-bottom: 3px solid #1976d2;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 10px;
          }
          .invoice-title {
            font-size: 24px;
            font-weight: bold;
            color: #d32f2f;
            margin-bottom: 10px;
          }
          .invoice-number {
            font-size: 18px;
            color: #666;
          }
          .customer-info {
            background: #f5f5f5;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            padding: 5px 0;
          }
          .label {
            font-weight: bold;
            color: #555;
            width: 30%;
          }
          .value {
            color: #333;
            width: 70%;
          }
          .services-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
          }
          .services-table th,
          .services-table td {
            border: 1px solid #ddd;
            padding: 12px;
            text-align: center;
          }
          .services-table th {
            background: #1976d2;
            color: white;
            font-weight: bold;
          }
          .services-table tr:nth-child(even) {
            background: #f9f9f9;
          }
          .total-section {
            background: #e3f2fd;
            padding: 15px;
            border-radius: 5px;
            margin: 20px 0;
            border: 2px solid #1976d2;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-size: 16px;
          }
          .total-final {
            font-size: 20px;
            font-weight: bold;
            color: #1976d2;
            border-top: 2px solid #1976d2;
            padding-top: 10px;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            border-top: 1px solid #ccc;
            padding-top: 20px;
            font-size: 14px;
            color: #666;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${systemSettings?.companyName || 'اليكترونيات السواح'}</div>
          <div class="invoice-title">فاتورة صيانة</div>
          <div class="invoice-number">رقم الفاتورة: ${orderNumber}</div>
        </div>

        <div class="customer-info">
          <h3 style="color: #1976d2; margin-bottom: 15px;">معلومات العميل</h3>
          <div class="info-row">
            <span class="label">اسم العميل:</span>
            <span class="value">${record.customerName}</span>
          </div>
          <div class="info-row">
            <span class="label">رقم الهاتف:</span>
            <span class="value">${customerData?.phone || customerData?.alternatePhone || 'غير محدد'}</span>
          </div>
          <div class="info-row">
            <span class="label">تاريخ الفاتورة:</span>
            <span class="value">${currentDate}</span>
          </div>
          <div class="info-row">
            <span class="label">تاريخ الصيانة:</span>
            <span class="value">${new Date(record.receivedAt).toLocaleDateString('ar-EG')}</span>
          </div>
        </div>

        <table class="services-table">
          <thead>
            <tr>
              <th>البيان</th>
              <th>التفاصيل</th>
              <th>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>صيانة ${record.deviceType}</td>
              <td>${record.issue}</td>
              <td>${formatCurrency(finalCost)}</td>
            </tr>
            ${record.usedParts && record.usedParts.length > 0 ?
              record.usedParts.map((part: any) => `
                <tr>
                  <td>قطعة غيار: ${part.name}</td>
                  <td>الكمية: ${part.quantity}</td>
                  <td>${formatCurrency(part.quantity * (part.sellingPrice || part.purchasePrice))}</td>
                </tr>
              `).join('') : ''
            }
          </tbody>
        </table>

        <div class="total-section">
          <div class="total-row">
            <span>أجرة الصيانة:</span>
            <span>${formatCurrency(finalCost)}</span>
          </div>
          ${partsTotal > 0 ? `
          <div class="total-row">
            <span>قطع الغيار:</span>
            <span>${formatCurrency(partsTotal)}</span>
          </div>
          ` : ''}
          <div class="total-row">
            <span>المبلغ المدفوع مقدماً:</span>
            <span>${formatCurrency(record.paid)}</span>
          </div>
          <div class="total-row total-final">
            <span>إجمالي الفاتورة:</span>
            <span>${formatCurrency(totalAmount)}</span>
          </div>
          <div class="total-row total-final">
            <span>المبلغ المتبقي:</span>
            <span>${formatCurrency(totalAmount - record.paid)}</span>
          </div>
        </div>

        <div class="footer">
          <p><strong>شكراً لثقتكم بنا</strong></p>
          <p>${systemSettings?.companyName || 'اليكترونيات السواح'} | هاتف: ${systemSettings?.companyPhone || '0123456789'}</p>
          <p>تاريخ الطباعة: ${currentDate}</p>
        </div>
      </body>
      </html>
    `;
  };

  // إغلاق رسالة التنبيه
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // حفظ الصور الملتقطة
  const handleSaveImages = async (images: any[]) => {
    try {
      // حفظ الصور في قاعدة البيانات
      if (record && id) {
        const updatedImages = [...(record.images || []), ...images];

        // تحديث سجل الصيانة بالصور الجديدة
        const maintenanceRef = doc(db, 'maintenance', id);
        await updateDoc(maintenanceRef, {
          images: updatedImages,
          updatedAt: Timestamp.now()
        });

        setMaintenanceImages(updatedImages);

        setSnackbar({
          open: true,
          message: `تم حفظ ${images.length} صورة بنجاح`,
          severity: 'success'
        });

        // إعادة تحميل بيانات الصيانة
        if (realId) loadMaintenanceRecord(realId);
      }
    } catch (error) {
      console.error('Error saving images:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حفظ الصور',
        severity: 'error'
      });
    } finally {
      setOpenWebcamDialog(false);
    }
  };

  // حذف صورة
  const handleDeleteImage = async (imageId: string) => {
    try {
      if (record && id) {
        const updatedImages = (record.images || []).filter((img: any) => img.id !== imageId);

        // تحديث سجل الصيانة
        const maintenanceRef = doc(db, 'maintenance', id);
        await updateDoc(maintenanceRef, {
          images: updatedImages,
          updatedAt: Timestamp.now()
        });

        setMaintenanceImages(updatedImages);

        setSnackbar({
          open: true,
          message: 'تم حذف الصورة بنجاح',
          severity: 'success'
        });

        // إعادة تحميل بيانات الصيانة
        if (realId) loadMaintenanceRecord(realId);
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء حذف الصورة',
        severity: 'error'
      });
    }
  };

  // مشاركة صورة
  const handleShareImage = (imageUrl: string) => {
    if (navigator.share) {
      navigator.share({
        title: 'صورة من طلب الصيانة',
        url: imageUrl
      });
    } else {
      // نسخ الرابط إلى الحافظة
      navigator.clipboard.writeText(imageUrl).then(() => {
        setSnackbar({
          open: true,
          message: 'تم نسخ رابط الصورة إلى الحافظة',
          severity: 'info'
        });
      });
    }
  };

  const handlePayment = async () => {
    if (!record || !id) return;

    try {
      setLoading(true);

      console.log('🔄 بدء معالجة الدفع:', {
        maintenanceId: id,
        paymentAmount,
        paymentType,
        userId: currentUser?.id,
        userName: currentUser?.name
      });

      // استخدام الوظيفة الجديدة لتحديث المدفوعات
      await updateMaintenancePayment(id, {
        amount: paymentAmount,
        paymentType: paymentType as 'cash' | 'debt',
        userId: currentUser?.id || '',
        userName: currentUser?.name || '',
        notes: `دفعة من ${record.customerName} - ${record.deviceType}`
      });

      console.log('✅ تم معالجة الدفع بنجاح');

      setSnackbar({
        open: true,
        message: `تم تسديد ${paymentAmount} جنيه ${paymentType === 'cash' ? 'نقداً' : 'ديناً'} بنجاح`,
        severity: 'success'
      });

      setShowPaymentDialog(false);
      if (realId) loadMaintenanceRecord(realId);
    } catch (error) {
      console.error('Error processing payment:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تسديد نقدي أو دين',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // عرض شاشة التحميل
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }



  // عرض رسالة في حالة عدم وجود طلب الصيانة
  if (!record) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ mb: 2 }}
        >
          العودة إلى قائمة طلبات الصيانة
        </Button>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            لم يتم العثور على طلب الصيانة. قد يكون تم حذفه أو حدث خطأ في التحميل.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => realId && loadMaintenanceRecord(realId)}
          >
            إعادة المحاولة
          </Button>
        </Paper>
      </Box>
    );
  }



  // تحديث الفني المرتبط بطلب الصيانة
  const updateTechnician = async () => {
    if (!selectedTechnician || !realId) {
      console.error('❌ لا يوجد فني محدد أو معرف طلب صيانة');
      return;
    }

    try {
      console.log('🔄 تحديث الفني لطلب الصيانة:', {
        maintenanceId: realId,
        oldTechnicianId: record?.technicianId,
        newTechnicianId: selectedTechnician.id,
        technicianName: selectedTechnician.name,
        salaryType: selectedTechnician.salaryType,
        customAmount: showCustomAmount ? customAmount : undefined
      });

      // معالجة تغيير الفني إذا كان هناك فني سابق
      if (record?.technicianId && record.technicianId !== selectedTechnician.id) {
        const { handleTechnicianChange } = await import('../services/maintenanceService');
        await handleTechnicianChange(
          realId,
          record.technicianId,
          selectedTechnician.id,
          record
        );
        console.log('✅ تم معالجة تغيير الفني بنجاح');
      }

      // تحديث طلب الصيانة بمعلومات الفني الجديد
      await updateMaintenanceRecord(realId, {
        technicianId: selectedTechnician.id,
        technicianName: selectedTechnician.name
      });

      // إذا كان نوع الراتب متغير وتم تحديد مبلغ مخصص، نحفظ أجر الفني مباشرة
      if (selectedTechnician.salaryType === 'variable' && showCustomAmount && customAmount > 0 && record) {
        try {
          console.log('💰 حفظ المبلغ المتغير للفني:', {
            technicianId: selectedTechnician.id,
            maintenanceId: realId,
            customAmount,
            finalCost: record.finalCost || record.initialCost || 0
          });

          const { recordTechnicianEarning } = await import('../services/technicianService');
          await recordTechnicianEarning(
            selectedTechnician.id,
            realId,
            record.deviceType || 'غير محدد',
            record.devices?.[0]?.size || '',
            record.finalCost || record.initialCost || 0,
            record.branchId,
            customAmount // تمرير المبلغ المخصص
          );
          console.log('✅ تم حفظ المبلغ المتغير للفني بنجاح');
        } catch (earningError) {
          console.error('❌ خطأ في حفظ أجر الفني:', earningError);
          // لا نوقف العملية إذا فشل حفظ الأجر
        }
      }

      // تحديث البيانات المحلية
      if (record) {
        setRecord({
          ...record,
          technicianId: selectedTechnician.id,
          technicianName: selectedTechnician.name
        });
      }

      // إغلاق الحوار وإعادة تعيين القيم
      setShowTechnicianDialog(false);
      setSelectedTechnician(null);
      setCustomAmount(0);
      setShowCustomAmount(false);

      // إظهار رسالة نجاح
      setSnackbar({
        open: true,
        message: `تم ربط الفني ${selectedTechnician.name} بطلب الصيانة بنجاح${showCustomAmount && customAmount > 0 ? ' مع حفظ المبلغ المخصص' : ''}`,
        severity: 'success'
      });

      console.log('✅ تم تحديث الفني بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تحديث الفني:', error);
      setSnackbar({
        open: true,
        message: 'حدث خطأ أثناء تحديث الفني',
        severity: 'error'
      });
    }
  };

  return (
    <Box sx={{ backgroundColor: 'grey.50', minHeight: '100vh', pb: 4 }}>
      {/* شريط العنوان */}
      <Paper
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 0,
          p: 3,
          mb: 3,
          boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <IconButton 
              onClick={handleGoBack} 
              sx={{ 
                mr: 2,
                backgroundColor: 'rgba(255,255,255,0.2)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.3)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" color="white" fontWeight="bold">
                🔧 تفاصيل طلب الصيانة
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                إدارة ومتابعة حالة الطلب
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* شريط الأزرار */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          mx: 3,
          borderRadius: 3,
          backgroundColor: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)'
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => realId && loadMaintenanceRecord(realId)}
            sx={{
              borderRadius: 2,
              borderWidth: 2,
              fontWeight: 600,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            تحديث
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PrintIcon />}
            onClick={() => setOpenBarcodeDialog(true)}
            sx={{
              borderRadius: 2,
              borderWidth: 2,
              fontWeight: 600,
              '&:hover': {
                borderWidth: 2,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            🏷️ طباعة ملصق
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrintReceipt}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            📝 طباعة إيصال
          </Button>

          {/* أزرار WhatsApp سريعة */}
          {customerData && (
            <>
              {record.status === 'in_progress' && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => hybridWhatsAppService.sendDeviceInProgressMessage(record)}
                  sx={{
                    borderRadius: 2,
                    borderWidth: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  💬 حالة التقدم
                </Button>
              )}

              {record.status === 'completed' && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => hybridWhatsAppService.sendDeviceCompletedMessage(record)}
                  sx={{
                    borderRadius: 2,
                    borderWidth: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  ✅ إشعار الاكتمال
                </Button>
              )}
            </>
          )}

          {/* مكون إرسال رسائل الواتساب */}
          {customerData && (
            <WhatsAppMessageSender
              customerName={record.customerName}
              customerPhone={customerData.phone || customerData.alternatePhone || ''}
              deviceType={record.deviceType}
              deviceBrand={record.devices && record.devices.length > 0 ? record.devices[0].brand : undefined}
              deviceSize={record.devices && record.devices.length > 0 ? record.devices[0].size : ''}
              cost={record.costAfterContact ? `${formatCurrency(record.costAfterContact)}` : (record.finalCost ? `${formatCurrency(record.finalCost)}` : undefined)}
              orderId={record.orderNumber ? `#${record.orderNumber}` : record.id.substring(0, 8)}
              requireCallBeforeMaintenance={record.requireCallBeforeMaintenance}
              onMessageSent={(success, messageType) => {
                if (success) {
                  let messageText = '';
                  switch (messageType) {
                    case 'deviceReceived':
                      messageText = 'رسالة استلام الجهاز';
                      break;
                    case 'deviceInProgress':
                      messageText = 'رسالة التكلفة أثناء العمل';
                      break;
                    case 'deviceCompleted':
                      messageText = 'رسالة اكتمال الإصلاح';
                      break;
                    default:
                      messageText = 'الرسالة';
                  }
                  setSnackbar({
                    open: true,
                    message: `تم إرسال ${messageText} بنجاح`,
                    severity: 'success'
                  });
                }
              }}
            />
          )}

          <Tooltip
            title={record?.status !== MaintenanceStatus.COMPLETED ?
              'يجب أن تكون حالة الصيانة "تم الإصلاح" لطباعة الضمان' :
              'طباعة شهادة ضمان للعميل'
            }
          >
            <span>
              <Button
                variant="contained"
                color="success"
                startIcon={<PrintIcon />}
                onClick={handlePrintWarranty}
                disabled={record?.status !== MaintenanceStatus.COMPLETED}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                🎖️ ضمان
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={record?.status !== MaintenanceStatus.COMPLETED ?
              'يجب أن تكون حالة الصيانة "تم الإصلاح" لطباعة الفاتورة' :
              'طباعة فاتورة نهائية للعميل'
            }
          >
            <span>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={handlePrintInvoice}
                disabled={record?.status !== MaintenanceStatus.COMPLETED}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(156, 39, 176, 0.4)'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                🧾 فاتورة
              </Button>
            </span>
          </Tooltip>

          {/* زر إرجاع الصيانة */}
          <Tooltip
            title={
              record?.status === MaintenanceStatus.DELIVERED || record?.status === MaintenanceStatus.RETURNED ?
                'لا يمكن إرجاع طلب تم تسليمه أو مرتجع بالفعل' :
                'إرجاع الجهاز للعميل مع رفض الإصلاح'
            }
          >
            <span>
              <Button
                variant="contained"
                color="warning"
                startIcon={<ReturnIcon />}
                onClick={() => setOpenReturnDialog(true)}
                disabled={
                  record?.status === MaintenanceStatus.DELIVERED ||
                  record?.status === MaintenanceStatus.RETURNED
                }
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(255, 152, 0, 0.4)'
                  },
                  '&:disabled': {
                    opacity: 0.5
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                ↩️ إرجاع
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Paper>

      {/* معلومات الطلب الرئيسية */}
      <Paper 
        elevation={0}
        sx={{ 
          p: 4, 
          mb: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid rgba(102, 126, 234, 0.1)'
        }}
      >
        <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid', borderColor: 'primary.main' }}>
          <Typography 
            variant="h5" 
            fontWeight="bold"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <DevicesIcon sx={{ color: 'primary.main' }} />
            معلومات الطلب
          </Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white !important',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)'
                },
                '& *': {
                  color: 'white !important'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DescriptionIcon sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>رقم الطلب</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">
                {record.orderNumber ? `#${record.orderNumber}` : record.id.substring(0, 8)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white !important',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(79, 172, 254, 0.3)'
                },
                '& *': {
                  color: 'white !important'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>اسم العميل</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">{record.customerName}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                borderRadius: 2,
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white !important',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 20px rgba(67, 233, 123, 0.3)'
                },
                '& *': {
                  color: 'white !important'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PhoneIcon sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>رقم الهاتف</Typography>
              </Box>
              <Typography variant="h6" fontWeight="bold">
                {customerData?.phone || customerData?.alternatePhone || 'غير محدد'}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: '2px solid',
                borderColor: 'primary.light',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.2)'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CalendarIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                <Typography variant="subtitle2" color="text.secondary">تاريخ الاستلام</Typography>
              </Box>
              <Typography variant="body1" fontWeight="600">
                {new Date(record.receivedAt).toLocaleDateString('ar-EG')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 2, 
                borderRadius: 2,
                bgcolor: 'grey.50',
                border: '2px solid',
                borderColor: 'info.light',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: 'info.main',
                  boxShadow: '0 4px 12px rgba(79, 172, 254, 0.2)'
                }
              }}
            >
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DevicesIcon sx={{ fontSize: 20, color: 'info.main' }} />
                <Typography variant="subtitle2" color="text.secondary">نوع الجهاز</Typography>
              </Box>
              <Typography variant="body1" fontWeight="600">{record.deviceType}</Typography>
            </Paper>
          </Grid>

          {/* تفاصيل الجهاز الإضافية */}
          {(record.devices && record.devices.length > 0) ? (
            // إذا كان هناك أجهزة متعددة، عرض تفاصيل الجهاز الأول
            <>
              {record.devices[0].brand && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      border: '2px solid',
                      borderColor: 'success.light',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'success.main',
                        boxShadow: '0 4px 12px rgba(67, 233, 123, 0.2)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white !important',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        🏷️
                      </Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }} fontWeight="600">
                        ماركة الجهاز
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: 'success.dark' }}>
                      {record.devices[0].brand}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {record.devices[0].model && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      border: '2px solid',
                      borderColor: 'secondary.light',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'secondary.main',
                        boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white !important',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        📱
                      </Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }} fontWeight="600">
                        موديل الجهاز
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: 'secondary.dark' }}>
                      {record.devices[0].model}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {record.devices[0].size && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      border: '2px solid',
                      borderColor: 'warning.light',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'warning.main',
                        boxShadow: '0 4px 12px rgba(255, 152, 0, 0.2)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white !important',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        📏
                      </Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }} fontWeight="600">
                        حجم الجهاز
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: 'warning.dark' }}>
                      {record.devices[0].size}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              {record.devices[0].serialNumber && (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      borderRadius: 2,
                      bgcolor: 'grey.50',
                      border: '2px solid',
                      borderColor: 'error.light',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: 'error.main',
                        boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      >
                        🔢
                      </Box>
                      <Typography variant="subtitle2" color="text.secondary" fontWeight="600">
                        الرقم التسلسلي
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight="600" color="error.dark" sx={{ fontFamily: 'monospace' }}>
                      {record.devices[0].serialNumber}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </>
          ) : (
            // للطلبات القديمة، محاولة عرض التفاصيل من الحقول المباشرة إذا كانت متوفرة
            <>
              {/* يمكن إضافة حقول إضافية هنا إذا كانت متوفرة في البيانات القديمة */}
            </>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">حالة الصيانة</Typography>
            <Box display="flex" alignItems="center">
              <Tooltip title={statusTooltips[record.status as MaintenanceStatus] || statusLabels[record.status as MaintenanceStatus]} placement="top">
                <Chip
                  icon={statusIcons[record.status as MaintenanceStatus]}
                  label={undefined}
                  color={statusColors[record.status as MaintenanceStatus] as any}
                  size="small"
                  sx={{ 
                    mr: 1,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    '& .MuiChip-icon': {
                      margin: 0,
                      fontSize: '1rem',
                      marginLeft: 0,
                      marginRight: 0
                    }
                  }}
                />
              </Tooltip>
              <Button
                variant="text"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => setOpenStatusDialog(true)}
              >
                تغيير
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">تاريخ التسليم المتوقع</Typography>
            <Typography variant="body1">
              {record.estimatedDeliveryDate ? new Date(record.estimatedDeliveryDate).toLocaleDateString('ar-EG') : 'غير محدد'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">الموظف المستلم</Typography>
            <Typography variant="body1">
              {record.receivedByEmployeeName || 'غير محدد'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">الفني المسؤول</Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="body1">
                {record.technicianName ? (
                  <Box display="flex" alignItems="center">
                    <EngineeringIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                    {record.technicianName}
                  </Box>
                ) : (
                  'لم يتم تحديد فني'
                )}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  console.log('🔘 فتح حوار اختيار الفني:', {
                    currentTechnicianId: record.technicianId,
                    currentTechnicianName: record.technicianName,
                    availableTechnicians: technicians.length,
                    branchId: record.branchId
                  });

                  // تعيين الفني الحالي إذا كان موجوداً
                  if (record.technicianId) {
                    const currentTechnician = technicians.find(t => t.id === record.technicianId);
                    console.log('🔍 البحث عن الفني الحالي:', currentTechnician ? 'موجود' : 'غير موجود');
                    setSelectedTechnician(currentTechnician || null);
                  } else {
                    setSelectedTechnician(null);
                  }

                  // إعادة تعيين القيم
                  setCustomAmount(0);
                  setShowCustomAmount(false);
                  setShowTechnicianDialog(true);
                }}
                sx={{ ml: 1 }}
              >
                {record.technicianName ? 'تغيير' : 'تحديد'}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">الفرع</Typography>
            <Typography variant="body1">
              {record.branchName || 'غير محدد'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">المخزن</Typography>
            <Typography variant="body1">
              {actualWarehouseName || record.warehouseName || 'غير محدد'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2" color="text.secondary">الاتصال قبل الصيانة</Typography>
            <Box display="flex" alignItems="center">
              <Chip
                label={record.requireCallBeforeMaintenance ? 'مطلوب' : 'غير مطلوب'}
                color={record.requireCallBeforeMaintenance ? 'primary' : 'default'}
                size="small"
                sx={{ mr: 1 }}
              />
              <Button
                variant="text"
                size="small"
                startIcon={<EditIcon />}
                onClick={() => {
                  // Abrir el diálogo de pago para editar el costo después de contactar
                  setShowCostAfterContact(true);
                  setCostAfterContact(record.costAfterContact || record.finalCost || record.initialCost);
                  setOpenPaymentDialog(true);
                }}
              >
                تعديل
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">المشكلة</Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>{record.issue}</Typography>
          </Grid>
          {record.diagnosis && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">التشخيص</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>{record.diagnosis}</Typography>
            </Grid>
          )}
          {record.accessories && record.accessories.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">الملحقات المستلمة</Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {record.accessories.join(', ')}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setOpenEditDialog(true)}
              sx={{ mb: 1 }}
            >
              تعديل بيانات العميل/الجهاز
            </Button>

            {/* زر اختبار مؤقت */}
            {process.env.NODE_ENV === 'development' && (
              <Button
                variant="outlined"
                size="small"
                color="secondary"
                onClick={async () => {
                  console.log('🧪 اختبار الصلاحيات...');
                  console.log('👤 المستخدم الحالي:', currentUser);
                  console.log('🔑 دور المستخدم:', currentUser?.role);
                  console.log('🆔 معرف المستخدم:', currentUser?.id);

                  try {
                    // اختبار بسيط لتحديث طلب الصيانة
                    await updateMaintenanceRecord(record.id, {
                      notes: record.notes || 'اختبار الصلاحيات'
                    });
                    console.log('✅ اختبار الصلاحيات نجح');
                    setSnackbar({
                      open: true,
                      message: 'اختبار الصلاحيات نجح',
                      severity: 'success'
                    });
                  } catch (error: any) {
                    console.error('❌ اختبار الصلاحيات فشل:', error);
                    setSnackbar({
                      open: true,
                      message: `اختبار الصلاحيات فشل: ${error?.code || error?.message}`,
                      severity: 'error'
                    });
                  }
                }}
                sx={{ mb: 1, ml: 1 }}
              >
                اختبار الصلاحيات
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* قسم الأجهزة المتعددة */}
      {record.devices && record.devices.length > 1 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            الأجهزة في هذا الطلب ({record.devices.length} أجهزة)
          </Typography>
          <Grid container spacing={2}>
            {record.devices.map((device, index) => (
              <Grid item xs={12} key={device.id || index}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    الجهاز {index + 1}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">نوع الجهاز</Typography>
                      <Typography variant="body2">{device.deviceType}</Typography>
                    </Grid>
                    {device.brand && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">الماركة</Typography>
                        <Typography variant="body2">{device.brand}</Typography>
                      </Grid>
                    )}
                    {device.model && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">الموديل</Typography>
                        <Typography variant="body2">{device.model}</Typography>
                      </Grid>
                    )}
                    {device.size && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">الحجم</Typography>
                        <Typography variant="body2">{device.size}</Typography>
                      </Grid>
                    )}
                    {device.serialNumber && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="subtitle2" color="text.secondary">الرقم التسلسلي</Typography>
                        <Typography variant="body2">{device.serialNumber}</Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="subtitle2" color="text.secondary">الحالة</Typography>
                      <Chip
                        label={statusLabels[device.status as MaintenanceStatus] || 'قيد الانتظار'}
                        color={statusColors[device.status as MaintenanceStatus] as any || 'default'}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">المشكلة</Typography>
                      <Typography variant="body2">{device.issue}</Typography>
                    </Grid>
                    {device.diagnosis && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">التشخيص</Typography>
                        <Typography variant="body2">{device.diagnosis}</Typography>
                      </Grid>
                    )}
                    {device.accessories && device.accessories.length > 0 && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">الملحقات</Typography>
                        <Typography variant="body2">{device.accessories.join(', ')}</Typography>
                      </Grid>
                    )}
                    {device.notes && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">ملاحظات</Typography>
                        <Typography variant="body2">{device.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* قطع الغيار المستخدمة */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">قطع الغيار المستخدمة</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setOpenEnhancedPartsDialog(true)}
              color="primary"
            >
              إضافة قطعة غيار
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenPartsDialog}
            >
              إضافة قطعة غيار (بسيط)
            </Button>
          </Box>
        </Box>



        {!record.usedParts || record.usedParts.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            لم يتم استخدام أي قطع غيار حتى الآن.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>القطعة</TableCell>
                  <TableCell>الكمية</TableCell>
                  <TableCell>سعر الوحدة</TableCell>
                  <TableCell>الإجمالي</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {record.usedParts && record.usedParts.map((part: any) => (
                  <TableRow key={part.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {part.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {part.id || part.itemId}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={part.quantity}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatCurrency(part.sellingPrice || part.purchasePrice)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatCurrency(part.quantity * (part.sellingPrice || part.purchasePrice))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="مخصوم من المخزون"
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<InventoryIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemovePart(part.id)}
                        title="حذف وإرجاع للمخزون"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* تنبيه خاص لحالة المرتجع */}
      {record.status === 'refunded' && (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: 'warning.light',
            border: '2px solid',
            borderColor: 'warning.main'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <RefundIcon sx={{ mr: 2, color: 'warning.dark', fontSize: 32 }} />
              <Box>
                <Typography variant="h6" color="warning.dark" fontWeight="bold">
                  طلب مرتجع
                </Typography>
                <Typography variant="body2" color="warning.dark">
                  هذا الطلب في حالة مرتجع. يمكنك إرجاع المبلغ المدفوع للعميل.
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="warning"
              size="large"
              startIcon={<RefundIcon />}
              onClick={() => setOpenRefundDialog(true)}
              sx={{
                minWidth: 150,
                fontWeight: 'bold'
              }}
            >
              إرجاع المبلغ
            </Button>
          </Box>
        </Paper>
      )}

      {/* ملخص التكلفة والدفع الجديد */}
      <CostSummaryCard
        maintenance={record}
        onSetInitialCost={() => setOpenInitialCostDialog(true)}
        onUpdateEstimatedCost={() => setOpenEstimatedCostDialog(true)}
        onConfirmApproval={() => setOpenCustomerApprovalDialog(true)}
        onDeliveryPayment={() => setOpenDeliveryPaymentDialog(true)}
        onPostDeliveryPayment={() => setOpenPostDeliveryPaymentDialog(true)}
        onAdvancePayment={() => setOpenAdvancePaymentDialog(true)}
        onViewPaymentHistory={() => setOpenPaymentHistoryDialog(true)}
        onRefund={() => setOpenRefundDialog(true)}
        showActions={true}
      />


      {/* ملاحظات إضافية */}
      {record.notes && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>ملاحظات</Typography>
          <Typography variant="body1">{record.notes}</Typography>
        </Paper>
      )}

      {/* قسم الصور */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">صور الجهاز والعيوب</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              onClick={() => setOpenImageSettingsDialog(true)}
            >
              إعدادات الرفع
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<PhotoCameraIcon />}
              onClick={() => setOpenWebcamDialog(true)}
            >
              التقاط صور
            </Button>
          </Box>
        </Box>

        <MaintenanceImageGallery
          images={maintenanceImages}
          onDelete={handleDeleteImage}
          onShare={handleShareImage}
          title="صور الجهاز"
        />

        {/* معلومات تشخيصية للصور */}
        {process.env.NODE_ENV === 'development' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              تشخيص الصور: {maintenanceImages.length} صورة محملة
            </Typography>
            {maintenanceImages.length > 0 && (
              <Typography variant="caption" display="block" color="text.secondary">
                أنواع الصور: {maintenanceImages.map(img => img.type).join(', ')}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* نافذة حوار تحديث الحالة */}
      <Dialog open={openStatusDialog} onClose={() => setOpenStatusDialog(false)}>
        <DialogTitle>تحديث حالة طلب الصيانة</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>الحالة</InputLabel>
            <Select
              value={selectedStatus}
              label="الحالة"
              onChange={(e) => setSelectedStatus(e.target.value as MaintenanceStatus)}
            >
              <MenuItem value="pending">
                <Tooltip title={statusTooltips[MaintenanceStatus.PENDING]} placement="right">
                  <span>قيد الانتظار</span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="in_progress">
                <Tooltip title={statusTooltips[MaintenanceStatus.IN_PROGRESS]} placement="right">
                  <span>جاري العمل</span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="completed">
                <Tooltip title={statusTooltips[MaintenanceStatus.COMPLETED]} placement="right">
                  <span>تم الإصلاح</span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="delivered">
                <Tooltip title={statusTooltips[MaintenanceStatus.DELIVERED]} placement="right">
                  <span>تم التسليم</span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="cancelled">
                <Tooltip title={statusTooltips[MaintenanceStatus.CANCELLED]} placement="right">
                  <span>مرتجع</span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="refunded">
                <Tooltip title={statusTooltips[MaintenanceStatus.REFUNDED]} placement="right">
                  <span>مرتجع مع استرداد</span>
                </Tooltip>
              </MenuItem>
              <MenuItem value="returned">
                <Tooltip title={statusTooltips[MaintenanceStatus.RETURNED]} placement="right">
                  <span>مرتجع مع رفض الجهاز</span>
                </Tooltip>
              </MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStatusDialog(false)}>إلغاء</Button>
          <Button onClick={handleUpdateStatus} variant="contained" color="primary">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة حوار إضافة قطع غيار */}
      <Dialog open={openPartsDialog} onClose={() => setOpenPartsDialog(false)}>
        <DialogTitle>إضافة قطعة غيار</DialogTitle>
        <DialogContent>
          {loadingInventory ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                <InputLabel>قطعة الغيار</InputLabel>
                <Select
                  value={selectedPart}
                  label="قطعة الغيار"
                  onChange={(e) => setSelectedPart(e.target.value)}
                >
                  {inventoryItems.length === 0 ? (
                    <MenuItem disabled>لا توجد قطع غيار متاحة</MenuItem>
                  ) : (
                    inventoryItems.map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name} (متاح: {item.quantity})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <TextField
                label="الكمية"
                type="number"
                fullWidth
                value={partQuantity}
                onChange={(e) => setPartQuantity(Math.max(1, parseInt(e.target.value) || 0))}
                InputProps={{
                  inputProps: { min: 1 }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPartsDialog(false)}>إلغاء</Button>
          <Button
            onClick={async () => {
              if (selectedPart) {
                try {
                  const inventoryService = await import('../services/inventoryService');
                  const item = await inventoryService.getInventoryItemById(selectedPart);
                  alert(`معلومات قطعة الغيار:\nالاسم: ${item?.name}\nسعر الشراء: ${item?.purchasePrice}\nسعر البيع: ${item?.sellingPrice}`);
                } catch (error) {
                  console.error('Error checking item:', error);
                  alert('حدث خطأ أثناء التحقق من قطعة الغيار');
                }
              }
            }}
            variant="outlined"
            color="info"
            disabled={loadingInventory || !selectedPart}
            sx={{ mr: 1 }}
          >
            فحص السعر
          </Button>
          <Button
            onClick={handleAddPart}
            variant="contained"
            color="primary"
            disabled={loadingInventory || !selectedPart || partQuantity < 1}
          >
            إضافة
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة حوار تعديل التكلفة والدفعات */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EditIcon color="primary" />
            تعديل التكلفة والدفعات
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box py={1}>
            {/* معلومات التكلفة الحالية */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                معلومات التكلفة الحالية
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">التكلفة المبدئية:</Typography>
                  <Typography variant="body2" fontWeight="medium">{formatCurrency(record?.initialCost || 0)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">المدفوع:</Typography>
                  <Typography variant="body2" fontWeight="medium" color="success.main">{formatCurrency(record?.paid || 0)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">التكلفة النهائية:</Typography>
                  <Typography variant="body2" fontWeight="medium" color="primary.main">{formatCurrency(record?.finalCost || 0)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">المتبقي:</Typography>
                  <Typography variant="body2" fontWeight="medium" color="error.main">{formatCurrency(calculateRemainingAmount())}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* خيارات التعديل */}
            <Box mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={record?.requireCallBeforeMaintenance || false}
                    onChange={(e) => {
                      if (record) {
                        setRecord({
                          ...record,
                          requireCallBeforeMaintenance: e.target.checked
                        });
                      }
                    }}
                  />
                }
                label="الاتصال بالعميل قبل الصيانة"
              />
            </Box>

            <Box mb={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showCostAfterContact}
                    onChange={(e) => {
                      setShowCostAfterContact(e.target.checked);
                      if (e.target.checked && record) {
                        setCostAfterContact(record.costAfterContact || record.finalCost || record.initialCost);
                      }
                    }}
                  />
                }
                label="تعديل التكلفة النهائية"
              />
            </Box>

            {/* تعديل التكلفة النهائية */}
            {showCostAfterContact && (
              <TextField
                label="التكلفة النهائية الجديدة"
                type="number"
                fullWidth
                sx={{ mb: 2 }}
                value={costAfterContact}
                onChange={(e) => {
                  const value = Math.max(0, parseFloat(e.target.value) || 0);
                  setCostAfterContact(value);
                }}
                InputProps={{
                  inputProps: { min: 0 },
                  endAdornment: <InputAdornment position="end">EGP</InputAdornment>
                }}
                helperText="التكلفة المتفق عليها بعد الاتصال بالعميل"
              />
            )}

            {/* إضافة دفعة */}
            <TextField
              label="المبلغ المدفوع الجديد"
              type="number"
              fullWidth
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(Math.max(0, parseInt(e.target.value) || 0))}
              InputProps={{
                inputProps: {
                  min: 0,
                  max: showCostAfterContact && costAfterContact > 0 ?
                    Math.max(0, costAfterContact - (record?.paid || 0)) :
                    calculateRemainingAmount()
                },
                endAdornment: <InputAdornment position="end">EGP</InputAdornment>
              }}
              helperText={`الحد الأقصى: ${formatCurrency(showCostAfterContact && costAfterContact > 0 ?
                Math.max(0, costAfterContact - (record?.paid || 0)) :
                calculateRemainingAmount())}`}
            />

            {/* ملاحظة */}
            <Box mt={2} p={1} bgcolor="info.light" borderRadius={1}>
              <Typography variant="caption" color="info.dark">
                💡 يمكنك تعديل التكلفة النهائية أو إضافة دفعة جديدة أو كليهما معاً
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>إلغاء</Button>
          <Button
            onClick={handleAddPayment}
            variant="contained"
            color="primary"
            disabled={
              // مُعطل إذا:
              // 1. لا يوجد دفع ولا يتم تعديل التكلفة
              (paymentAmount <= 0 && (!showCostAfterContact || costAfterContact <= 0)) ||
              // 2. يوجد دفع لكنه يتجاوز المبلغ المتبقي
              (paymentAmount > 0 && paymentAmount > (showCostAfterContact && costAfterContact > 0 ?
                Math.max(0, costAfterContact - (record?.paid || 0)) :
                calculateRemainingAmount()))
            }
          >
            {paymentAmount > 0 && showCostAfterContact && costAfterContact > 0 ?
              'تحديث التكلفة وتسديد نقدي أو دين' :
              paymentAmount > 0 ? 'تسديد نقدي أو دين' :
              showCostAfterContact && costAfterContact > 0 ? 'تحديث التكلفة' :
              'حفظ التغييرات'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار الدفع */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)}>
        <DialogTitle>تسديد نقدي أو دين</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            المبلغ المتبقي: {formatCurrency(calculateRemainingAmount())}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="المبلغ المدفوع"
            type="number"
            fullWidth
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(Number(e.target.value))}
            error={paymentAmount > calculateRemainingAmount()}
            helperText={paymentAmount > calculateRemainingAmount() ? 'المبلغ المدفوع أكبر من المبلغ المتبقي' : ''}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>نوع الدفع</InputLabel>
            <Select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value as 'cash' | 'debt')}
              label="نوع الدفع"
            >
              <MenuItem value="cash">نقدي</MenuItem>
              <MenuItem value="debt">دين</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>إلغاء</Button>
          <Button
            onClick={handlePayment}
            variant="contained"
            color="primary"
            disabled={loading || paymentAmount > calculateRemainingAmount()}
          >
            {loading ? <CircularProgress size={24} /> : 'تسديد نقدي أو دين'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* رسائل التنبيه */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* نافذة حوار طباعة الباركود */}
      {record && (
        <PrintBarcodeDialog
          open={openBarcodeDialog}
          onClose={() => setOpenBarcodeDialog(false)}
          maintenance={record}
        />
      )}

      {/* نافذة حوار التقاط الصور */}
      <WebcamCapture
        open={openWebcamDialog}
        onClose={() => setOpenWebcamDialog(false)}
        onSave={handleSaveImages}
        maintenanceId={id || ''}
        deviceInfo={{
          brand: record?.devices?.[0]?.brand,
          model: record?.devices?.[0]?.model,
          serialNumber: record?.devices?.[0]?.serialNumber
        }}
      />

      {/* نافذة حوار إعدادات مزود الصور */}
      <ImageProviderSettings
        open={openImageSettingsDialog}
        onClose={() => setOpenImageSettingsDialog(false)}
        onSave={(provider) => {
          console.log('Selected provider:', provider);
          setSnackbar({
            open: true,
            message: `تم تحديد ${provider} كمزود خدمة رفع الصور`,
            severity: 'success'
          });
        }}
      />

      {/* نافذة حوار اختيار قطع الغيار المحسنة */}
      <EnhancedPartsSelector
        open={openEnhancedPartsDialog}
        onClose={() => setOpenEnhancedPartsDialog(false)}
        onSelectPart={handleEnhancedPartSelect}
        warehouseId={record?.warehouseId}
      />

      {/* نوافذ النظام الجديد */}
      {record && (
        <>
          <InitialCostDialog
            open={openInitialCostDialog}
            onClose={() => setOpenInitialCostDialog(false)}
            maintenanceId={record.id}
            customerName={record.customerName}
            deviceType={record.deviceType}
            userId={currentUser?.id || ''}
            onSuccess={() => {
              if (realId) loadMaintenanceRecord(realId);
              setSnackbar({
                open: true,
                message: 'تم تسجيل التكلفة المبدئية بنجاح',
                severity: 'success'
              });
            }}
          />

          <EstimatedCostDialog
            open={openEstimatedCostDialog}
            onClose={() => setOpenEstimatedCostDialog(false)}
            maintenance={record}
            userId={currentUser?.id || ''}
            onSuccess={() => {
              if (realId) loadMaintenanceRecord(realId);
              setSnackbar({
                open: true,
                message: 'تم تحديث التكلفة المقدرة بنجاح',
                severity: 'success'
              });
            }}
          />

          <DeliveryPaymentDialog
            open={openDeliveryPaymentDialog}
            onClose={() => setOpenDeliveryPaymentDialog(false)}
            maintenance={record}
            userId={currentUser?.id || ''}
            userName={currentUser?.name || ''}
            onSuccess={() => {
              if (realId) loadMaintenanceRecord(realId);
              setSnackbar({
                open: true,
                message: 'تم تسليم الجهاز أو تسجيله كمرتجع بنجاح',
                severity: 'success'
              });
            }}
          />

          <CustomerApprovalDialog
            open={openCustomerApprovalDialog}
            onClose={() => setOpenCustomerApprovalDialog(false)}
            maintenance={record}
            userId={currentUser?.id || ''}
            onSuccess={() => {
              if (realId) loadMaintenanceRecord(realId);
              setSnackbar({
                open: true,
                message: 'تم تسجيل موافقة العميل بنجاح',
                severity: 'success'
              });
            }}
          />

          <PostDeliveryPaymentDialog
            open={openPostDeliveryPaymentDialog}
            onClose={() => setOpenPostDeliveryPaymentDialog(false)}
            maintenance={record}
            userId={currentUser?.id || ''}
            userName={currentUser?.name || ''}
            onSuccess={() => {
              if (realId) loadMaintenanceRecord(realId);
              setSnackbar({
                open: true,
                message: 'تم تسديد نقدي أو دين بنجاح',
                severity: 'success'
              });
            }}
          />

          <AdvancePaymentDialog
            open={openAdvancePaymentDialog}
            onClose={() => setOpenAdvancePaymentDialog(false)}
            maintenance={record}
            userId={currentUser?.id || ''}
            userName={currentUser?.name || ''}
            onSuccess={() => {
              if (realId) loadMaintenanceRecord(realId);
              setSnackbar({
                open: true,
                message: 'تم تسجيل الدفعة المقدمة بنجاح',
                severity: 'success'
              });
            }}
          />

          <PaymentHistoryDialog
            open={openPaymentHistoryDialog}
            onClose={() => setOpenPaymentHistoryDialog(false)}
            maintenanceId={record?.id || ''}
            customerName={record?.customerName || ''}
          />

          <MaintenanceRefundDialog
            open={openRefundDialog}
            onClose={() => setOpenRefundDialog(false)}
            maintenance={record}
            onRefund={handleRefund}
          />

          {/* حوار تعديل الفني */}
          <Dialog
            open={showTechnicianDialog}
            onClose={() => setShowTechnicianDialog(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <Box display="flex" alignItems="center">
                <EngineeringIcon sx={{ mr: 1, color: 'primary.main' }} />
                تحديد الفني المسؤول
              </Box>
            </DialogTitle>
            <DialogContent>
              {/* معلومات تشخيصية */}
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" display="block">
                    🔍 تشخيص: {technicians.length} فني متاح، جاري التحميل: {loadingTechnicians ? 'نعم' : 'لا'}
                  </Typography>
                  <Typography variant="caption" display="block">
                    📋 معرف الفرع: {record?.branchId || 'غير محدد'}
                  </Typography>
                </Box>
              )}

              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>اختر الفني</InputLabel>
                <Select
                  value={selectedTechnician?.id || ''}
                  onChange={(e) => {
                    const technician = technicians.find(t => t.id === e.target.value);
                    setSelectedTechnician(technician || null);

                    // إظهار حقل المبلغ المتغير إذا كان نوع الراتب متغير
                    console.log('🔍 تحقق من نوع راتب الفني:', {
                      technicianName: technician?.name,
                      salaryType: technician?.salaryType,
                      isVariable: technician?.salaryType === 'variable'
                    });

                    if (technician?.salaryType === 'variable') {
                      setShowCustomAmount(true);

                      // حساب مبلغ افتراضي بناءً على تكلفة الصيانة
                      let defaultAmount = technician.salaryAmount || 0;
                      if (defaultAmount === 0 && record) {
                        const baseCost = record.finalCost || record.initialCost || 0;
                        if (baseCost > 0) {
                          defaultAmount = Math.round(baseCost * 0.15); // 15% من التكلفة
                        } else {
                          defaultAmount = 150; // مبلغ افتراضي
                        }
                      }

                      console.log('💰 تعيين مبلغ افتراضي للفني المتغير:', {
                        defaultAmount,
                        baseCost: record?.finalCost || record?.initialCost || 0,
                        technicianSalaryAmount: technician.salaryAmount
                      });

                      setCustomAmount(defaultAmount);
                    } else {
                      setShowCustomAmount(false);
                      setCustomAmount(0);
                    }
                  }}
                  label="اختر الفني"
                  disabled={loadingTechnicians}
                >
                  <MenuItem value="">
                    <em>لا يوجد فني محدد</em>
                  </MenuItem>
                  {technicians.length > 0 ? (
                    technicians.map((technician) => (
                      <MenuItem key={technician.id} value={technician.id}>
                        <Box display="flex" alignItems="center">
                          <EngineeringIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 16 }} />
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {technician.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {technician.specialization}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        {loadingTechnicians ? 'جاري التحميل...' : 'لا يوجد فنيين في هذا الفرع'}
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* عرض معلومات نوع الراتب */}
              {selectedTechnician && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    معلومات الراتب
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        نوع الراتب
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedTechnician.salaryType === 'fixed' && 'راتب ثابت'}
                        {selectedTechnician.salaryType === 'per_device' && 'بالقطعة'}
                        {selectedTechnician.salaryType === 'percentage' && 'بالنسبة المئوية'}
                        {selectedTechnician.salaryType === 'variable' && 'قطعة متغيرة'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        المبلغ الافتراضي
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {selectedTechnician.salaryAmount}
                        {selectedTechnician.salaryType === 'percentage' ? '%' : ' جنيه'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* حقل المبلغ المتغير */}
              {showCustomAmount && selectedTechnician && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="مبلغ مخصص للفني (جنيه)"
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(Number(e.target.value))}
                    helperText={`المبلغ الافتراضي: ${selectedTechnician.salaryAmount} جنيه`}
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ mr: 1, color: 'text.secondary' }}>
                          💰
                        </Box>
                      )
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    يمكنك تعديل المبلغ حسب صعوبة الإصلاح أو نوع الجهاز
                  </Typography>
                </Box>
              )}

              {/* معاينة الحساب */}
              {selectedTechnician && record && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    معاينة حساب الأجر
                  </Typography>
                  {selectedTechnician.salaryType === 'fixed' && (
                    <Typography variant="body2">
                      راتب ثابت: <strong>{selectedTechnician.salaryAmount} جنيه</strong>
                    </Typography>
                  )}
                  {selectedTechnician.salaryType === 'per_device' && (
                    <Typography variant="body2">
                      أجر بالقطعة: <strong>{selectedTechnician.salaryAmount} جنيه</strong>
                    </Typography>
                  )}
                  {selectedTechnician.salaryType === 'percentage' && (
                    <Typography variant="body2">
                      نسبة {selectedTechnician.salaryAmount}% من {record.finalCost || record.initialCost || 0} =
                      <strong> {((record.finalCost || record.initialCost || 0) * selectedTechnician.salaryAmount / 100).toFixed(2)} جنيه</strong>
                    </Typography>
                  )}
                  {selectedTechnician.salaryType === 'variable' && (
                    <Typography variant="body2">
                      مبلغ متغير: <strong>{customAmount} جنيه</strong>
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setShowTechnicianDialog(false);
                  setSelectedTechnician(null);
                  setCustomAmount(0);
                  setShowCustomAmount(false);
                }}
              >
                إلغاء
              </Button>
              <Button
                onClick={() => {
                  console.log('🔘 نقر على زر تحديث الفني:', {
                    selectedTechnician: selectedTechnician?.name,
                    salaryType: selectedTechnician?.salaryType,
                    showCustomAmount,
                    customAmount,
                    recordStatus: record?.status
                  });
                  updateTechnician();
                }}
                variant="contained"
                disabled={!selectedTechnician || (showCustomAmount && customAmount <= 0)}
                startIcon={<EngineeringIcon />}
              >
                {record?.technicianId ? 'تحديث الفني' : 'تحديد الفني'}
              </Button>
            </DialogActions>
          </Dialog>


        </>
      )}
      {/* Dialog التعديل */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)}>
        <DialogTitle>تعديل بيانات العميل أو الجهاز</DialogTitle>
        <DialogContent>
          <TextField
            label="اسم العميل"
            fullWidth
            margin="dense"
            value={editForm.name}
            onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
          />
          <TextField
            label="رقم الهاتف"
            fullWidth
            margin="dense"
            value={editForm.phone}
            onChange={e => {
              // السماح بالأرقام فقط
              const value = e.target.value.replace(/[^0-9]/g, '');
              setEditForm(f => ({ ...f, phone: value }));
            }}
            inputProps={{
              maxLength: 12,
              pattern: '[0-9]*'
            }}
            helperText="أدخل رقم الهاتف (أرقام فقط)"
          />
          <Autocomplete
            options={deviceSizes}
            autoHighlight
            getOptionLabel={(option) => {
              if (option.name === 'small') return 'صغير';
              if (option.name === 'medium') return 'متوسط';
              if (option.name === 'large') return 'كبير';
              if (option.name === 'extraLarge') return 'كبير جداً';
              return option.name;
            }}
            value={deviceSizes.find(s => s.name === editForm.size) || null}
            onChange={(event, newValue) => {
              setEditForm(f => ({ ...f, size: newValue ? newValue.name : '' }));
            }}
            loading={loadingDeviceSettings}
            renderInput={(params) => (
              <TextField
                {...params}
                label="حجم الشاشة/الجهاز"
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingDeviceSettings ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            noOptionsText="لا توجد أحجام"
          />
          <Autocomplete
            options={deviceBrands}
            autoHighlight
            getOptionLabel={(option) => option.name}
            value={deviceBrands.find(b => b.name === editForm.brand) || null}
            onChange={(event, newValue) => {
              setEditForm(f => ({ ...f, brand: newValue ? newValue.name : '' }));
            }}
            loading={loadingDeviceSettings}
            renderInput={(params) => (
              <TextField
                {...params}
                label="ماركة الجهاز"
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingDeviceSettings ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            noOptionsText="لا توجد ماركات"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>إلغاء</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={savingEdit}
            onClick={async () => {
              setSavingEdit(true);
              try {
                console.log('🔄 بدء حفظ التعديلات...');
                console.log('📝 بيانات النموذج:', editForm);
                console.log('📋 بيانات الطلب الحالية:', record);
                console.log('👤 المستخدم الحالي:', currentUser);
                console.log('🔥 حالة Firebase:', {
                  authenticated: !!currentUser,
                  userId: currentUser?.id,
                  userRole: currentUser?.role,
                  recordId: record?.id
                });

                // التحقق من صحة البيانات
                if (!editForm.name.trim()) {
                  setSnackbar({
                    open: true,
                    message: 'يجب إدخال اسم العميل',
                    severity: 'error'
                  });
                  return;
                }

                if (!editForm.phone.trim()) {
                  setSnackbar({
                    open: true,
                    message: 'يجب إدخال رقم الهاتف',
                    severity: 'error'
                  });
                  return;
                }

                // تحديث بيانات العميل
                if (record.customerId) {
                  console.log('👤 تحديث بيانات العميل:', record.customerId);
                  try {
                    await updateCustomer(record.customerId, {
                      name: editForm.name.trim(),
                      phone: editForm.phone.trim()
                    });
                    console.log('✅ تم تحديث بيانات العميل');
                  } catch (customerError: any) {
                    console.error('❌ فشل في تحديث بيانات العميل:', customerError);
                    // نتابع العملية حتى لو فشل تحديث العميل
                  }
                }

                // تحديث بيانات الجهاز في سجل الصيانة
                const updateData: any = {
                  customerName: editForm.name.trim() // تحديث اسم العميل في سجل الصيانة أيضاً
                };

                // تحديث بيانات الجهاز الأول إذا وُجد
                if (record.devices && record.devices[0]) {
                  console.log('📱 تحديث بيانات الجهاز...');
                  const newDevices = [...record.devices];
                  const currentDevice = newDevices[0];

                  // التأكد من أن الحجم صحيح أو الاحتفاظ بالقيمة الحالية
                  const selectedSize = deviceSizes.find(size => size.name === editForm.size);
                  const safeSize = selectedSize ? selectedSize.name as DeviceSize : currentDevice.size;

                  // التأكد من أن الماركة ليست فارغة
                  const safeBrand = editForm.brand.trim() || currentDevice.brand || '';

                  newDevices[0] = {
                    ...currentDevice,
                    size: safeSize,
                    brand: safeBrand
                  };

                  updateData.devices = newDevices;

                  // تحديث الحقول القديمة للتوافق (فقط إذا كانت موجودة)
                  if (currentDevice.deviceType) {
                    updateData.deviceType = currentDevice.deviceType;
                  }
                  if (safeBrand) {
                    updateData.brand = safeBrand;
                  }
                  if (safeSize) {
                    updateData.size = safeSize;
                  }

                  console.log('📱 بيانات الجهاز الجديدة:', newDevices[0]);
                }

                console.log('💾 تحديث سجل الصيانة...');
                console.log('📝 البيانات المراد تحديثها:', updateData);
                console.log('🆔 معرف الطلب:', record.id);

                // التحقق من وجود بيانات للتحديث
                if (Object.keys(updateData).length === 0) {
                  console.warn('⚠️ لا توجد بيانات للتحديث');
                  setSnackbar({
                    open: true,
                    message: 'لا توجد تغييرات للحفظ',
                    severity: 'info'
                  });
                  return;
                }

                await updateMaintenanceRecord(record.id, updateData);
                console.log('✅ تم تحديث سجل الصيانة بنجاح');

                // إعادة تحميل البيانات
                console.log('🔄 إعادة تحميل البيانات...');
                await loadMaintenanceRecord(record.id);

                setSnackbar({
                  open: true,
                  message: 'تم حفظ التعديلات بنجاح',
                  severity: 'success'
                });

                setOpenEditDialog(false);
                console.log('✅ تم حفظ التعديلات بنجاح');

              } catch (error: any) {
                console.error('❌ خطأ في حفظ التعديلات:', error);
                console.error('📋 تفاصيل الخطأ:', {
                  code: error?.code,
                  message: error?.message,
                  stack: error?.stack
                });

                let errorMessage = 'حدث خطأ أثناء حفظ التعديلات';

                if (error?.code === 'permission-denied') {
                  errorMessage = 'ليس لديك صلاحية لتعديل هذه البيانات. تحقق من دورك في النظام.';
                  console.error('🚫 خطأ صلاحيات - تحقق من دور المستخدم:', currentUser?.role);
                } else if (error?.code === 'not-found') {
                  errorMessage = 'لم يتم العثور على البيانات المراد تعديلها';
                } else if (error?.code === 'unavailable') {
                  errorMessage = 'الخدمة غير متاحة حالياً. حاول مرة أخرى.';
                } else if (error?.code === 'unauthenticated') {
                  errorMessage = 'انتهت جلسة المصادقة. يرجى تسجيل الدخول مرة أخرى.';
                } else if (error?.code === 'failed-precondition') {
                  errorMessage = 'فشل في شروط التحديث. تحقق من صحة البيانات.';
                } else if (error?.message) {
                  errorMessage = `خطأ: ${error.message}`;
                }

                setSnackbar({
                  open: true,
                  message: errorMessage,
                  severity: 'error'
                });
              } finally {
                setSavingEdit(false);
              }
            }}
          >حفظ</Button>
        </DialogActions>
      </Dialog>

      {/* حوار خيارات الطباعة */}
      <PrintOptionsDialog
        open={openPrintOptionsDialog}
        onClose={() => {
          setOpenPrintOptionsDialog(false);
          setPendingPrintAction(null);
        }}
        onPrint={handlePrintWithOptions}
        title="خيارات الطباعة"
        documentType={pendingPrintAction || 'receipt'}
      />

      {/* حوار إرجاع الصيانة */}
      <MaintenanceReturnDialog
        open={openReturnDialog}
        onClose={() => setOpenReturnDialog(false)}
        maintenance={record}
        onReturnCreated={handleReturnCreated}
      />
    </Box>
  );
};

export default MaintenanceDetailsPage;